import { isMacAppStore } from '@/lib/appDistribution';
import React, { useEffect, useRef, useCallback, useState, useMemo, useImperativeHandle, useContext } from 'react';
import { StyleSheet, Platform, Animated, View, FlatList, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { HeaderHeightContext } from '@react-navigation/elements';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getShadowThemeStyles } from '@/constants/Colors';
import { useAuthStore, useRouteStore, useChatStore, useUIStore, useToastStore, useAgentStore, useModalStore, useChatInputStore, useVoiceModeStore, useToolStore, useModelStore, useCoworkerStore } from '@/store';
import ChatInput from '@/components/ChatInput/ChatInput';
import Message from '@/components/Messages/Message';
import MessageGroup from '@/components/Messages/MessageGroup';
import ConversationSummaryPill from '@/components/Messages/ConversationSummaryPill';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import useChatKeyboardScroll from '@/hooks/useChatKeyboardScroll';
import ChatComposerOverlay from '@/components/Chat/ChatComposerOverlay';
import { useAuthPresentation } from '@/hooks/useRequireAuth';
import useAppActive from '@/hooks/useAppActive';
import socketManager from '@/lib/socket';
import { isDesktopLinuxWeb, isDesktopLinuxChromiumWeb } from '@/lib/desktopLinux';
import { createChatScrollRecovery } from '@/constants/utils/chat_scroll_recovery';
import { createChatScrollAnchor } from '@/constants/utils/chat_scroll_anchor';
import { getChatPrependOffset, trackChatPrependScroll } from '@/constants/utils/chat_prepend_scroll';
import { workQuestionContinuation } from '@/lib/workQuestionContinuation';
import { insertWorkQuestionHistory } from '@/lib/workQuestionHistory';
import WorkQuestionHistoryRow from '@/components/Coworker/WorkQuestionHistoryRow';
import { useMacAccessRequests } from '@/lib/macAccessRequests';
import WorkAccessRequests from '@/components/Coworker/WorkAccessRequests';
import { chatsApi } from '@/lib/api';
import voiceModeService from '@/lib/voiceMode';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import MessageWaiting from '@/components/Messages/MessageWaiting';
import MessageGhostUpsell from '@/components/Messages/MessageGhostUpsell';
import MessageDateSeparator from '@/components/Messages/MessageDateSeparator';
import PlusMessageLockBanner from '@/components/Messages/PlusMessageLockBanner';
import PlusLockedMessageCeiling from '@/components/Messages/PlusLockedMessageCeiling';
import MediaGenerationSuggestions from '@/components/Messages/MediaGenerationSuggestions';
import FollowUpSuggestions from '@/components/Messages/FollowUpSuggestions';
import ToolIntentSuggestions from '@/components/ToolIntentSuggestions';
import WorkModeIntentSuggestions from '@/components/WorkModeIntentSuggestions';
import WorkModeSuggestions from '@/components/Messages/WorkModeSuggestions';
import useToolIntentSuggestions from '@/hooks/useToolIntentSuggestions';
import useWorkModeIntentSuggestions from '@/hooks/useWorkModeIntentSuggestions';
import useCollectionSuggestion from '@/hooks/useCollectionSuggestion';
import useRequireAuth from '@/hooks/useRequireAuth';
import CollectionSuggestionBanner from '@/components/CollectionSuggestionBanner';
import ChatCollectionBadge from '@/components/Chat/ChatCollectionBadge';
import {
  resolveImageRequestUserQuery,
  resolveImageRequestUserQueryForGroup,
} from '@/constants/utils/tool_intent_matcher';
import {
  resolveWorkModeSuggestion,
  getPendingWorkModeMessage,
  getFileIds,
} from '@/constants/utils/work_mode_intent_matcher';
import { startWorkFromPrompt } from '@/lib/startWorkFromPrompt';
import reportWorkLaunchError from '@/lib/reportWorkLaunchError';
import WorkLaunchWaitNotice from '@/components/Coworker/WorkLaunchWaitNotice';
import { isWorkModeDisabledForUser } from '@/lib/workModeAvailability';
import {
  resolveMediaGenerationContext,
  resolveMediaGenerationContextForGroup,
} from '@/constants/utils/media_model_suggestions';
import { isArtifactSidebarType, isDesktopEditorSidebarType, getOpenVideoProjectProps, maybeAutoOpenVideoProject, openMessageFile } from '@/constants/utils/files';
import CardExpirationWarningMessage from '@/components/Messages/CardExpirationWarningMessage';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH_AMOUNTS, getNavigationSidebarWidth, getEffectiveContextSidebarWidth } from '@/constants/config';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import MacProjectReadOnlyNotice from '@/components/Coworker/MacProjectReadOnlyNotice';
import WorkTaskLoadingNotice from '@/components/Coworker/WorkTaskLoadingNotice';
import { hasMacTaskBoundary, resolveMacProjectReadOnly } from '@/lib/macProjectReadOnly';
import useRemoteMacConnection from '@/hooks/useRemoteMacConnection';
import RemoteMacComposerNotice from '@/components/Remote/RemoteMacComposerNotice';
import CoworkerLivePanel from '@/components/Coworker/CoworkerLivePanel';
import { WorkApprovalCard, WorkPlanCard, WorkQuestionCard } from '@/components/Coworker/WorkActionCards';
import WorkTaskBoundaryCard from '@/components/Coworker/WorkTaskBoundaryCard';
import WorkTaskStartedNotice from '@/components/Coworker/WorkTaskStartedNotice';
import WorkNotificationNotice from '@/components/Coworker/WorkNotificationNotice';
import WorkAppear from '@/components/Coworker/WorkAppear';
import WorkSignInComposer from '@/components/Coworker/WorkSignInComposer';
import WorkFollowUpQueue from '@/components/Coworker/WorkFollowUpQueue';
import BrowserViewer from '@/components/Coworker/BrowserViewer';
import useEmbeddedBrowser from '@/hooks/useEmbeddedBrowser';
import { WorkLiveProvider, useWorkLiveValue } from '@/components/Coworker/WorkLiveContext';
import { WorkNotesVisibilityProvider } from '@/components/Coworker/WorkNotesVisibility';
import { WorkLiveActivityRow, WorkTranscriptMessage, WorkUserTranscriptRow } from '@/components/Coworker/WorkTranscriptRows';
import WorkUpgradeNotice from '@/components/Coworker/WorkUpgradeNotice';
import {
  buildWorkRuns,
  buildWorkTurns,
  workMessageProjectionKey,
  workSourcesFromEvents,
  workProjectSourcesFromEvents,
  workUserMessageForDisplay,
  WORK_FOLLOWUP_PLACEHOLDER,
  WORK_STEER_PLACEHOLDER,
  shouldShowWorkLiveActivity,
  shouldShowWorkPinnedPlan,
  workPlanAttachIndex,
  WORK_THINKING_STATUS,
} from '@/constants/work';
import { isWorkFollowUpQueueMessage } from '@/constants/work_followup_queue';
import { useCoworkerChat } from '@/hooks/useCoworkerChat';
import { useConversationAgentHydration } from '@/hooks/useMessageAgents';
import { useWorkModeHandover } from '@/hooks/useWorkModeHandover';
import { canAccessPlusOnlyModel, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import ChatMessagesSkeleton from '@/components/Placeholders/ChatMessagesSkeleton';
import ChatLoadingState from '@/components/Placeholders/ChatLoadingState';
import ConversationTimelineRail from '@/components/Chat/ConversationTimelineRail';
import ConversationFindBar from '@/components/Chat/ConversationFindBar';
import SourcesOutputsPanel, { PANEL_TOP_OFFSET as SOURCES_OUTPUTS_PANEL_TOP } from '@/components/Chat/SourcesOutputsPanel';
import WorkSidebar from '@/components/Coworker/WorkSidebar';
import { MAC_MIN_CHAT_WIDTH, getMacSidebarWidth, isCompactMacWindow } from '@/lib/macResponsiveLayout';
import { resolveSourcesOutputsLayout, resolveSourcesOutputsReservedRight } from '@/components/Chat/sourcesOutputsLayout';
import ConversationForkOrigin from '@/components/Chat/ConversationForkOrigin';
import SideChatLayout from '@/components/Chat/SideChatLayout';
import markdownToPlainText from '@/constants/utils/markdown_to_plain_text';
import { collectSourcesAndOutputs } from '@/constants/utils/sources_outputs';
import { collectVideoStudioInventory, loadVideoStudioScript } from '@/lib/videoStudioInventory';
import {
  buildVideoProject,
  firstVideoProjectMedia,
  conversationMediaStudio,
  isImageMedia,
  isObsoleteVideoFollowUp,
  videoProjectReviewGate,
  workVideoReviewQuestion,
} from '@/constants/utils/video_project';
import { useTranslation } from '@/hooks/useTranslation';
import useWorkChatTasks from '@/hooks/useWorkChatTasks';
import { saveLastChatScroll, clearLastChatScroll } from '@/lib/lastRoute';
import {
  findGroupedMessageIndex,
  scrollMessageNodeIntoView,
  scrollMessageNodeToStart,
  scrollSearchMatchIntoView,
} from '@/constants/utils/conversation_find';
import { isMobileReadingChat, shouldHoldChatScroll } from '@/constants/utils/chat_scroll_policy';
import { createChatIndexScroller } from '@/constants/utils/chat_index_scroll';
import { createAndroidChatScroller } from '@/constants/utils/android_chat_scroll';
import { createWorkScrollFollower } from '@/constants/utils/work_scroll_follower';
import {
  findStreamingReply,
  getStreamScrollMode,
  shouldRestoreStudioScroll,
  shouldShowScrollToBottomButton,
  WORK_STREAM_SCROLL,
} from '@/constants/utils/work_stream_scroll';

const EMPTY_COMPOSER_FILES = [];

// Sources / Outputs card (232px) + its 12px right offset + breathing room
// before the message column starts. Keep in sync with the panel's own sizing.

function MessageHighlightWrapper({ messageIds, children }) {
  const highlightedId = useUIStore(state => state.highlightedMessageId);
  const searchQuery = useUIStore(state => state.highlightedSearchQuery);
  const persistHighlight = useUIStore(state => state.highlightedMessagePersist);
  const clearHighlightedMessageId = useUIStore(state => state.clearHighlightedMessageId);
  const { colors } = useTheme();
  const ids = messageIds || [];
  const isHighlighted = !!highlightedId && ids.includes(highlightedId);
  const animatedOpacity = useRef(new Animated.Value(0)).current;
  const isWeb = Platform.OS === 'web';
  const domId = isWeb ? `msg-hl-${(isHighlighted && highlightedId) || ids[0] || 'x'}` : undefined;

  useEffect(() => {
    if (!isHighlighted) return;

    if (isWeb && typeof document !== 'undefined') {
      if (!document.getElementById('search-hl-css')) {
        const s = document.createElement('style');
        s.id = 'search-hl-css';
        s.textContent = `
          @keyframes searchHlPulse { 0%{opacity:0} 5%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
          mark.search-hl {
            background-color: rgba(250,200,21,0.35) !important;
            color: inherit !important; border-radius: 3px !important;
            padding: 1px 2px !important; display: inline !important;
            box-decoration-break: clone !important; -webkit-box-decoration-break: clone !important;
            box-shadow: 0 0 0 3px rgba(250,200,21,0.25) !important;
            animation: searchHlPulse 6s ease-out forwards !important;
          }
          mark.search-hl-persist {
            animation: none !important;
            opacity: 1 !important;
          }`;
        document.head.appendChild(s);
      }

      const q = searchQuery?.toLowerCase();
      let applied = false;

      const tryApply = () => {
        if (applied || !q) return true;
        const el = document.getElementById(domId);
        if (!el?.textContent) return false;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        while (walker.nextNode()) {
          const tv = walker.currentNode.nodeValue;
          const idx = tv?.toLowerCase().indexOf(q);
          if (idx == null || idx === -1) continue;
          const node = walker.currentNode;
          const p = node.parentNode;
          if (!p) continue;
          const frag = document.createDocumentFragment();
          if (idx > 0) frag.appendChild(document.createTextNode(tv.slice(0, idx)));
          const mark = document.createElement('mark');
          mark.className = persistHighlight ? 'search-hl search-hl-persist' : 'search-hl';
          mark.textContent = tv.slice(idx, idx + searchQuery.length);
          frag.appendChild(mark);
          if (idx + searchQuery.length < tv.length) frag.appendChild(document.createTextNode(tv.slice(idx + searchQuery.length)));
          p.replaceChild(frag, node);
          applied = true;
          return true;
        }
        return false;
      };

      let tries = 0;
      const poll = setInterval(() => {
        if (tryApply() || ++tries > 40) clearInterval(poll);
      }, 150);
      tryApply();

      const expire = persistHighlight ? null : setTimeout(() => {
        clearInterval(poll);
        try {
          const el = document.getElementById(domId);
          if (el) el.querySelectorAll('mark.search-hl').forEach(m => {
            const p = m.parentNode;
            if (p) { p.replaceChild(document.createTextNode(m.textContent || ''), m); p.normalize(); }
          });
        } catch (_) {}
        clearHighlightedMessageId();
      }, 7000);

      return () => {
        clearInterval(poll);
        if (expire) clearTimeout(expire);
        try {
          const el = document.getElementById(domId);
          if (el) el.querySelectorAll('mark.search-hl').forEach(m => {
            const p = m.parentNode;
            if (p) { p.replaceChild(document.createTextNode(m.textContent || ''), m); p.normalize(); }
          });
        } catch (_) {}
      };
    }

    if (persistHighlight) {
      animatedOpacity.setValue(1);
      return undefined;
    }

    animatedOpacity.setValue(0);
    Animated.sequence([
      Animated.timing(animatedOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
      Animated.delay(4000),
      Animated.timing(animatedOpacity, { toValue: 0, duration: 800, useNativeDriver: true }),
    ]).start(() => clearHighlightedMessageId());
  }, [isHighlighted, persistHighlight, searchQuery, highlightedId, domId, isWeb, animatedOpacity, clearHighlightedMessageId]);

  if (isWeb) {
    return <View nativeID={domId}>{children}</View>;
  }

  if (isHighlighted) {
    return (
      <View style={{ position: 'relative' }}>
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: -2, left: -4, right: -4, bottom: -2,
            backgroundColor: colors.accent_color_primary || '#FFD700',
            borderWidth: 1.5,
            borderColor: colors.accent_color_primary || '#FFD700',
            borderRadius: 8,
            opacity: animatedOpacity.interpolate({ inputRange: [0, 1], outputRange: [0, 0.18] }),
          }}
        />
        {children}
      </View>
    );
  }

  return children;
}

const baseStyles = StyleSheet.create({
  content: {
    flex: 1,
    position: 'relative',
    flexDirection: 'row',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
  messagesContainer: {
    flex: 1,
    position: 'relative',
    flexDirection: 'row',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
  transcriptContainer: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
});

function renderListSlot(Component) {
  if (!Component) return null;
  if (typeof Component === 'function') return <Component />;
  return Component;
}

/**
 * RN Web FlatList windowing drops unmeasured Work rows and then treats the
 * short content height as the end of the thread. A plain ScrollView keeps
 * every row mounted so scrolling back down reaches the latest messages.
 */
function WebWorkTranscriptList({
  listRef,
  data,
  renderItem,
  keyExtractor,
  onScroll,
  onContentSizeChange,
  onLayout,
  contentContainerStyle,
  ListHeaderComponent,
  ListFooterComponent,
  style,
}) {
  const scrollRef = useRef(null);
  const nodeOf = () => scrollRef.current?.getScrollableNode?.() || scrollRef.current;

  useImperativeHandle(listRef, () => ({
    scrollToOffset: ({ offset = 0, animated = false } = {}) => {
      const node = nodeOf();
      if (node?.scrollTo) node.scrollTo({ y: offset, animated });
      else if (node) node.scrollTop = offset;
    },
    scrollToEnd: ({ animated = false } = {}) => {
      const node = nodeOf();
      const top = node?.scrollHeight || 1e8;
      if (node?.scrollTo) node.scrollTo({ y: top, animated });
      else if (node) node.scrollTop = top;
    },
    scrollToIndex: ({ index = 0, animated = false, viewOffset = 12 } = {}) => {
      const node = nodeOf();
      const child = node?.querySelector?.(`#work-row-${index}`);
      if (child && node) {
        const next = Math.max(0, child.offsetTop - viewOffset);
        if (node.scrollTo) node.scrollTo({ y: next, animated });
        else node.scrollTop = next;
      }
    },
  }), []);

  return (
    <ScrollView
      ref={scrollRef}
      style={[{ flex: 1, minHeight: 0 }, style]}
      contentContainerStyle={contentContainerStyle}
      onScroll={onScroll}
      scrollEventThrottle={16}
      onContentSizeChange={onContentSizeChange}
      onLayout={onLayout}
    >
      {renderListSlot(ListHeaderComponent)}
      {(data || []).map((item, index) => (
        <View key={keyExtractor(item, index)} nativeID={`work-row-${index}`}>
          {renderItem({ item, index })}
        </View>
      ))}
      {renderListSlot(ListFooterComponent)}
    </ScrollView>
  );
}

const ChatView = React.memo(({
  conversationId,
  chat: chatProp,
  isLoading,
  error,
  onRetryError,
  isCompact = false,
  voiceModeTarget,
  onFocus,
}) => {
  const id = Array.isArray(conversationId) ? conversationId[0] : conversationId;
  const storeChat = useChatStore((state) => (id ? state.chats.find((c) => c.id === id) || null : null));
  const chat = storeChat || chatProp;
  const router = useRouter();
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const themeStyles = getShadowThemeStyles(theme);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const fetchArchivedMessages = useChatStore(state => state.fetchArchivedMessages);
  const isChatInStore = useChatStore(state => state.chats.some(c => c.id === conversationId));
  const messageWidth = useUIStore(state => state.messageWidth);
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const { setCurrentLibraryId } = useRouteStore(state => state.actions);
  const isAuthenticated = useAuthPresentation();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const user = useAuthStore(state => state.user);
  const isPlus = user?.flags?.isPlus;
  const workModeDisabled = isWorkModeDisabledForUser(user);
  const isHeldForReview = user?.flags?.heldForReview === true;
  const canUseGatedModels = canAccessPlusOnlyModel(user);
  // Mirrors the backend inference gate (permissions_validator_service):
  // free accounts flagged as requiring verification must verify before sending.
  const needsEmailVerification = useAuthStore(state =>
    !state.user?.flags?.isPlus &&
    !!state.user?.flags?.isEmailVerificationRequired && !state.user?.flags?.isEmailVerified
  );
  // Plus/admin users are exempt from the SMS gate on the backend, so don't
  // UI-block them here either.
  const needsPhoneVerification = useAuthStore(state =>
    !!state.user?.flags?.smsVerificationRequired && !state.user?.flags?.isPhoneVerified
    && !state.user?.flags?.isPlus && !state.user?.flags?.isAdmin
  );
  const userId = useAuthStore(state => state.user?.id);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const { isXS, showsScrollIndicator, windowWidth, windowHeight } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const navigationHeaderHeight = useContext(HeaderHeightContext) ?? 0;
  const flatListRef = useRef(null);
  const indexScroller = useMemo(() => createChatIndexScroller(() => flatListRef.current), []);
  const scrollRequestRef = useRef(null);
  const scrollRequestTimerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollElementRef = useRef(null);
  const getScrollElement = useCallback(() => {
    if (Platform.OS !== 'web' || !messagesContainerRef.current) return null;
    const cached = scrollElementRef.current;
    if (cached && cached.isConnected && cached.offsetHeight >= 100) return cached;
    const el = [...(messagesContainerRef.current.querySelectorAll('*') || [])].find((e) => {
      if (e.offsetHeight < 100) return false;
      const s = window.getComputedStyle(e);
      return s.overflowY === 'auto' || s.overflowY === 'scroll';
    }) || null;
    scrollElementRef.current = el;
    return el;
  }, []);
  const inputContainerRef = useRef(null);
  // Always-latest pointer to requestPinToBottom so handlers defined *above* its
  // declaration (e.g. handleSend) can invoke it without a temporal-dead-zone
  // forward reference in their dependency array.
  const requestPinToBottomRef = useRef(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Detected after mount so SSR / first paint match Mac/Windows (windowSize 101).
  // Linux then tightens virtualization — see flatListProps.
  const [isLinuxDesktopWeb, setIsLinuxDesktopWeb] = useState(false);
  const [isLinuxChromiumWeb, setIsLinuxChromiumWeb] = useState(false);
  // Discard measurements on chat changes even when the list DOM is reused.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const linuxScrollRecovery = useMemo(() => createChatScrollRecovery(), [id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chatScrollAnchor = useMemo(() => createChatScrollAnchor(), [id]);
  useEffect(() => {
    if (Platform.OS === 'web') {
      setIsLinuxDesktopWeb(isDesktopLinuxWeb());
      setIsLinuxChromiumWeb(isDesktopLinuxChromiumWeb());
    }
  }, []);
  const lastMessageId = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const showScrollButtonRef = useRef(false);
  const showScrollButtonRafRef = useRef(0);
  const pendingShowScrollButtonRef = useRef(null);
  // RN Web fires onContentSizeChange during the layout pass. setState there
  // after a Work follow-up (new bubble + start-pin + live row) nested updates
  // until React hit Maximum update depth. Coalesce onto the next frame and
  // skip when the visibility did not change.
  const scheduleShowScrollButton = useCallback((next) => {
    const value = !!next;
    pendingShowScrollButtonRef.current = value;
    if (showScrollButtonRafRef.current) return;
    if (showScrollButtonRef.current === value) return;
    const apply = () => {
      showScrollButtonRafRef.current = 0;
      const scheduled = pendingShowScrollButtonRef.current;
      if (showScrollButtonRef.current === scheduled) return;
      showScrollButtonRef.current = scheduled;
      setShowScrollButton(scheduled);
    };
    if (typeof requestAnimationFrame === 'function') {
      showScrollButtonRafRef.current = requestAnimationFrame(apply);
      return;
    }
    apply();
  }, []);
  const scrollButtonAnimation = useRef(new Animated.Value(0)).current;
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const keyboardHeight = useComposerKeyboardHeight();
  const [inputContainerHeight, setInputContainerHeight] = useState(0);
  const isAtBottomRef = useRef(true);
  // Normal chat follows tokens only after an explicit jump to the bottom.
  // Work has its own bounded activity follower below.
  const followLiveRef = useRef(false);
  const workQuestionReplyRef = useRef(null);
  const chatScrollStateRef = useRef({});
  const studioUserTurnRef = useRef(null);
  const scrollOffsetRef = useRef(0);
  const listLayoutHeightRef = useRef(0);
  const contentHeightRef = useRef(0);
  // Drawer navigation reuses ChatView, but the keyed list has new geometry.
  // Clear old measurements before the new list can request a bottom scroll.
  const scrollMetricsIdRef = useRef(id);
  if (scrollMetricsIdRef.current !== id) {
    scrollMetricsIdRef.current = id;
    scrollOffsetRef.current = 0;
    listLayoutHeightRef.current = 0;
    contentHeightRef.current = 0;
    isAtBottomRef.current = true;
  }
  const lastScrollTime = useRef(0);
  // A content reflow can move FlatList's reported offset upward even when the
  // user did not touch the transcript. Keep a short-lived, explicit gesture
  // signal so those layout corrections do not disable follow-live.
  const userScrollIntentUntilRef = useRef(0);
  // True from onScrollBeginDrag until the drag (and any momentum) ends, so a
  // long finger-drag or flick still counts as user intent past the 1s window.
  const isUserScrollingRef = useRef(false);
  const lastDownwardScrollAtRef = useRef(0);
  const streamingReplyRef = useRef(null);
  const keyboardAnimatingRef = useRef(false);
  // Pin-to-bottom mechanism: persists through lazy-rendering content growth.
  // Long chats render messages progressively, so a single scrollToEnd call stops
  // short of the real bottom. We keep re-pinning on every content-size change
  // until content stabilizes (debounce) or a hard deadline passes.
  const wantsBottomRef = useRef(false);
  const bottomStabilizeTimerRef = useRef(null);
  const bottomMaxTimerRef = useRef(null);
  // Scheduled follow-up scroll attempts (rAF + short timeouts) that re-assert
  // the bottom after the current render commits and before the round-tripped
  // message arrives.
  const bottomRepinHandlesRef = useRef([]);
  const initialScrolledIdRef = useRef(null);
  const archiveScrollHydrationRef = useRef({ key: null, didTargetedLoad: false, loadsRemaining: 0 });
  const prependCompensationRef = useRef(null);
  // Anchor-pin mechanism (cold-start restore): after the initial
  // scroll-to-anchor, late-rendering content (markdown, code, math, images)
  // keeps changing row heights above the anchor, shoving it around the
  // viewport. Mirror of the bottom-pin: silently re-pin the anchor on every
  // content-size change until layout stabilizes or the deadline passes.
  // { messageId, viewPosition, searchQuery?, until } | null
  const anchorPinRef = useRef(null);
  const repinAnchorNowRef = useRef(null);
  // Settle veil (native): opening a long chat positions the list (bottom-pin
  // or restore anchor) while row heights are still estimates; every async
  // measurement (markdown / code / images) then visibly shakes the list as
  // the pin corrects. Instead of showing that, we hide the list (opacity 0)
  // during initial positioning and fade it in once content has been quiet
  // briefly — so the user only ever sees the final, stable position.
  const listSettleOpacity = useRef(new Animated.Value(1)).current;
  const settleVeilActiveRef = useRef(false);
  const settleVeilQuietTimerRef = useRef(null);
  const settleVeilMaxTimerRef = useRef(null);
  // Mounts the chat skeleton overlay while the veil is up; the skeleton's
  // opacity is the inverse of the list's, giving a cross-fade on reveal.
  const [isSettleVeilUp, setIsSettleVeilUp] = useState(false);
  // Work tool trails attach once the run's event log arrives; the settle veil
  // waits on this so they don't pop in after the list is revealed.
  const workEventsReadyRef = useRef(true);
  // Lets the web scroll-intent listener (declared above endListSettleVeil)
  // drop the veil without a temporal-dead-zone forward reference.
  const endListSettleVeilRef = useRef(null);
  const settleSkeletonOpacity = useMemo(
    () => listSettleOpacity.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
    [listSettleOpacity],
  );
  const pendingScrollRequest = useUIStore(state => state.pendingScrollRequest);
  const requestScrollToMessage = useUIStore(state => state.requestScrollToMessage);
  const clearPendingScrollRequest = useUIStore(state => state.clearPendingScrollRequest);
  const pendingScrollToTopRequest = useUIStore(state => state.pendingScrollToTopRequest);
  const clearPendingScrollToTopRequest = useUIStore(state => state.clearPendingScrollToTopRequest);
  const [isNearTop, setIsNearTop] = useState(true);

  // ── Scroll-position memory ────────────────────────────────────────────────
  // Remember the top-most visible message of the most-recently-active chat so a
  // cold-start restore (see useRestoreLastRoute) can drop the user back where
  // they were reading instead of at the bottom. We persist the anchor *message
  // id* (not a raw offset) so restoration can reuse the chat's robust
  // scroll-to-message flow, which already tolerates async layout growth.
  const idRef = useRef(id);
  idRef.current = id;
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  const scrollPersistTimerRef = useRef(null);
  // Top-most visible real message; drives the timeline rail's active tick.
  const [viewableAnchorId, setViewableAnchorId] = useState(null);
  // Mirror of viewableAnchorId for callbacks that must stay referentially
  // stable (handleLoadEarlierMessages) while still reading the latest anchor.
  const viewableAnchorIdRef = useRef(null);
  // Work threads never render the timeline rail, so the anchor only needs to
  // live in the ref there. Skipping the state update keeps a fast scroll from
  // re-rendering (and re-measuring) the whole transcript on every viewability
  // change, which is what makes media-heavy Work rows shuffle mid-gesture.
  const tracksAnchorInStateRef = useRef(true);
  const viewabilityConfigRef = useRef({ itemVisiblePercentThreshold: 10, minimumViewTime: 200 });
  const onViewableItemsChangedRef = useRef((info) => {
    const viewableItems = info?.viewableItems;
    if (isCompactRef.current) return;
    if (!viewableItems || viewableItems.length === 0) return;

    // First viewable item that maps to a real message (skip synthetic rows like
    // the summary pill, waiting indicator, ghost / card-expiration messages).
    let anchorId = null;
    for (let i = 0; i < viewableItems.length; i += 1) {
      const item = viewableItems[i]?.item;
      if (!item) continue;
      if (item.type === 'group') {
        anchorId = item.messages?.[0]?.id || null;
      } else if (item.role && item.id && !item.type
        && item.id !== 'inferencing-waiting' && item.id !== 'ghost-message') {
        anchorId = item.id;
      }
      if (anchorId) break;
    }
    if (!anchorId) return;

    viewableAnchorIdRef.current = anchorId;
    if (tracksAnchorInStateRef.current) setViewableAnchorId(anchorId);

    const convId = idRef.current;
    if (!convId || convId === 'new' || String(convId).startsWith('new-')) return;

    if (scrollPersistTimerRef.current) clearTimeout(scrollPersistTimerRef.current);
    scrollPersistTimerRef.current = setTimeout(() => {
      // At the bottom → forget any anchor so the next restore lands naturally.
      if (isAtBottomRef.current) {
        clearLastChatScroll();
      } else {
        saveLastChatScroll(idRef.current, anchorId);
      }
    }, 600);
  });

  useEffect(() => () => {
    if (scrollPersistTimerRef.current) clearTimeout(scrollPersistTimerRef.current);
    if (showScrollButtonRafRef.current && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(showScrollButtonRafRef.current);
      showScrollButtonRafRef.current = 0;
    }
  }, []);

  const effectiveChatInputStyle = isCompact ? CHAT_INPUT_STYLE.FIXED : chatInputStyle;
  const isDetailed = effectiveChatInputStyle === CHAT_INPUT_STYLE.DETAILED;

  const updateInputContainerHeight = useCallback((height) => {
    const nextHeight = Math.ceil(height || 0);
    setInputContainerHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  }, []);

  const handleInputContainerLayout = useCallback((event) => {
    updateInputContainerHeight(event?.nativeEvent?.layout?.height);
  }, [updateInputContainerHeight]);

  // RN Web's onLayout can run before Firefox has settled Linux font metrics or
  // after only part of the composer stack has rendered. Observe the actual DOM
  // box as well so list padding follows textarea, banner, and font reflows.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof ResizeObserver === 'undefined') return undefined;
    const node = inputContainerRef.current;
    if (!node) return undefined;

    const observer = new ResizeObserver((entries) => {
      updateInputContainerHeight(entries[0]?.contentRect?.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [updateInputContainerHeight, isAuthenticated, chat?.isOwner]);

  // Voice mode state
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const voiceModeConversationId = useVoiceModeStore(state => state.conversationId);
  const stopVoiceMode = useVoiceModeStore(state => state.actions.stopVoiceMode);

  // Card expiration warning state
  const cardExpirationWarning = useAuthStore(state => state.cardExpirationWarning);
  const clearCardExpirationWarning = useAuthStore(state => state.clearCardExpirationWarning);
  const [showCardExpirationWarning, setShowCardExpirationWarning] = useState(false);

  useEffect(() => {
    if (
      cardExpirationWarning?.shouldDisplay &&
      cardExpirationWarning?.conversationId === id
    ) {
      setShowCardExpirationWarning(true);
    }
  }, [cardExpirationWarning?.shouldDisplay, cardExpirationWarning?.conversationId, id]);

  const handleDismissCardExpirationWarning = useCallback(() => {
    setShowCardExpirationWarning(false);
    clearCardExpirationWarning();
  }, [clearCardExpirationWarning]);

  // Auto-end voice mode when unmounting this view
  useEffect(() => {
    const currentConversationId = id;
    return () => {
      const voiceStore = useVoiceModeStore.getState();
      if (voiceStore.isActive && voiceStore.conversationId === currentConversationId) {
        console.log('[ChatView] Unmounting voice conversation, ending voice mode');
        voiceModeService.endSession().catch(err => {
          console.warn('[ChatView] Error ending voice session:', err);
        });
        voiceStore.actions.stopVoiceMode();
      }
    };
  }, [id]);

  const isOwner = chat?.isOwner;
  const chatTitle = chat?.title;

  // Coworker unification (docs/COWORKER_CONVERSATION_UNIFICATION.md): a
  // coworker thread masquerades as a live conversation — the hook keeps the
  // chat's isInferencing flag in sync with task liveness so the composer
  // (Stop button, send gating) and list UI work unchanged.
  const coworker = useCoworkerChat({ chat, conversationId: id });
  const storeTaskDetailsPending = isMacAppStore() && coworker.isCoworkerChat && !hasMacTaskBoundary(coworker.task);
  const isMacProjectChat = !!(chat?.hasMacProject || coworker.task?.localProject?.bindingId);
  const remoteMacConnection = useRemoteMacConnection({
    enabled: isMacProjectChat && isAuthenticated && isOwner,
    hostId: coworker.task?.remoteProject?.hostId || coworker.task?.projectFolder?.hostId,
    deviceId: coworker.task?.localProject?.deviceId,
  });
  // Empty history does not establish revoked access: a failed initial launch
  // still needs its native Resume control, even before any rows have arrived.
  const isMacProjectReadOnly = resolveMacProjectReadOnly({
    platform: Platform.OS,
    isMacProjectChat: isMacProjectChat || (isMacAppStore() && !!coworker.task?.localDesktop?.grantId),
    revokedAt: coworker.task?.localProject?.revokedAt,
    remoteConnection: remoteMacConnection,
    taskDetailsPending: storeTaskDetailsPending,
  });
  useConversationAgentHydration(id);
  // The intent engine can upgrade a chat to Work mid-send. The hook above
  // joins the run immediately; the UI switches at the trough of a short fade
  // so the composer and transcript change together instead of popping.
  // See useWorkModeHandover.
  const handleWorkHandoverSwap = useCallback((toWork) => {
    if (!toWork || !id) return;
    // The chat "thinking" bubble held the spot through the fade; releasing it
    // here lets the Work activity line take over in the same render.
    useChatStore.getState().updateLocalChatDetails(id, {
      isInferencingWaiting: false,
      pendingInferenceStatus: undefined,
      pendingInferenceStage: undefined,
    });
  }, [id]);
  const { workMode: isCoworkerChat, fadeStyle: workHandoverStyle } = useWorkModeHandover(
    coworker.isCoworkerChat,
    { onSwap: handleWorkHandoverSwap },
  );
  const coworkerSend = coworker.send;
  const coworkerEnqueue = coworker.enqueueFollowUp;
  const coworkerSendFollowUpNow = coworker.sendFollowUpNow;
  const coworkerStop = coworker.stop;
  const coworkerHasSnapshot = coworker.hasSnapshot;
  const coworkerIsActive = coworker.isActive;
  const waitingForMacAccess = useMacAccessRequests(state =>
    state.requests.some(request => request.taskId === String(coworker.taskId))
    || state.issues.some(issue => issue.taskId === String(coworker.taskId)));

  // The durable web queue owns continuation once it saves an answer. Older
  // callers still use the host's requiresResume decision for a settled gate.
  const handleWorkQuestionAnswered = useCallback((text, { skipped, requiresResume, continuationQueued, followUpQueue, followUpQueueRevision, replyMessage } = {}) => {
    if (continuationQueued) {
      useCoworkerStore.getState().setFollowUpQueue(coworker.taskId, followUpQueue || [], followUpQueueRevision);
      void useCoworkerStore.getState().fetchTask(coworker.taskId);
      return;
    }
    const message = workQuestionContinuation(replyMessage || text, { skipped, requiresResume, isActive: coworkerIsActive });
    if (!message) return;
    // A gate response may become a user row, but it is not a new composer
    // send. Preserve the current following/reading state when it appears.
    workQuestionReplyRef.current = message.trim();
    // Preserve the actual answer, including any instruction to wait. Take
    // choices already live in durable Work events; the local UI cache may
    // predate a newer selection made in chat or another studio window.
    return coworkerSend(message);
  }, [coworkerIsActive, coworkerSend, coworker.taskId]);

  useEffect(() => {
    if (!isCoworkerChat) return undefined;
    return () => {
      try {
        const { Image } = require('expo-image');
        Image.clearMemoryCache?.();
      } catch { /* optional — native web cache only */ }
    };
  }, [isCoworkerChat, id]);

  const isInferencing = chat?.isInferencing || (isCoworkerChat && coworker.isActive);
  const isInferencingWaiting = chat?.isInferencingWaiting;
  const mobileReadingChat = isMobileReadingChat(Platform.OS, isCoworkerChat);

  // HTTP-initiated runs (prompts) can miss socket events if the client
  // navigates after the backend already finished. Poll until the server
  // clears flags.isInferencing, then refresh the thread.
  //
  // Gated on `isAppActive` so a backgrounded/hidden tab doesn't keep polling
  // every 4s forever. When the tab returns to the foreground the effect
  // re-runs and fires an immediate poll, so we pick up the finished state
  // without waiting out the first 4s tick.
  const isAppActive = useAppActive();
  useEffect(() => {
    // Coworker threads: liveness comes from the task's SSE feed
    // (useCoworkerChat), not the conversation-inference flags this poll
    // reconciles — polling here would never see the flag clear and loop.
    if (isCoworkerChat) return;
    if (!id || (!isInferencing && !isInferencingWaiting) || !isAppActive) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await chatsApi.getConversationStatus(id);
        if (cancelled || status?.isInferencing) return;
        const data = await chatsApi.getConversationById(id);
        if (cancelled || !data) return;
        useChatStore.getState().addChats([data]);
      } catch (_) {
        // Best-effort fallback — socket path is primary.
      }
    };

    // Immediate poll on becoming active — the server may have finished while
    // we were paused, and we don't want to wait the full 4s for the first
    // status check after focus/foreground.
    poll();
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, isInferencing, isInferencingWaiting, isAppActive, isCoworkerChat]);

  // Follow-up suggestions state
  const followUpSuggestions = useChatStore(state => state.followUpSuggestions[id]);
  const clearFollowUpSuggestions = useChatStore(state => state.clearFollowUpSuggestions);
  // Respect the user's opt-out instantly — even if the server emitted a set of
  // suggestions before the toggle was flipped, the UI should hide them now.
  const followUpSuggestionsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.followUpSuggestionsEnabled !== false
  );
  const workModeSuggestionsEnabled = useAuthStore(
    state => state.user?.flags?.isPlus === true
      && state.user?.flags?.isGhost !== true
      && state.user?.preferences?.chat?.workModeSuggestionsEnabled !== false
  );
  const showFollowUps = followUpSuggestionsEnabled
    && !isInferencing
    && !isInferencingWaiting
    && !(isCoworkerChat && coworker.isActive)
    && followUpSuggestions?.suggestions?.length > 0;

  // "Move this chat to <collection>?" suggestion (auto-categorization).
  // Rare by design (server-side throttled, once per chat ever), so when it is
  // present it takes priority over the follow-up suggestions strip.
  const {
    suggestion: collectionSuggestion,
    isResponding: isRespondingToCollectionSuggestion,
    accept: acceptCollectionSuggestion,
    dismiss: dismissCollectionSuggestion,
  } = useCollectionSuggestion(id);

  // Composer text drives which suggestion strip is visible above the input:
  // follow-ups when empty, tool-intent chips when a tool match is found.
  // Tool/memory upsells are suppressed while a desktop editor is open.
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isContextSidebarVisibleForSuggestions = useUIStore(state => state.isContextSidebarVisible);
  const isDesktopEditorOpen = isContextSidebarVisibleForSuggestions
    && isDesktopEditorSidebarType(contextSidebarType);
  const isVideoStudioOpen = isContextSidebarVisibleForSuggestions
    && contextSidebarType === 'video-project-preview';
  const isVideoStudioOpenRef = useRef(isVideoStudioOpen);
  isVideoStudioOpenRef.current = isVideoStudioOpen;
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const el = getScrollElement();
    if (!el) return undefined;
    const previous = el.style.overflowAnchor;
    el.style.overflowAnchor = 'none';
    return () => {
      el.style.overflowAnchor = previous;
    };
  }, [getScrollElement, isCoworkerChat, isVideoStudioOpen]);
  const { hasSuggestions: hasToolIntentSuggestions, message: composerText } = useToolIntentSuggestions(id);
  const {
    shouldSuggest: hasWorkModeIntentSuggestions,
    message: workComposerText,
    files: workComposerFiles,
  } = useWorkModeIntentSuggestions(id);
  const isComposerEmpty = !composerText.trim();
  const showCollectionSuggestionBar = !!collectionSuggestion
    && !chat?.inCollectionId
    && isComposerEmpty
    && !isInferencing
    && !isInferencingWaiting;
  const showFollowUpsBar = showFollowUps && isComposerEmpty && !showCollectionSuggestionBar;
  const showWorkIntentBar = hasWorkModeIntentSuggestions && !isDesktopEditorOpen && !isCoworkerChat;
  // Work replaces the tool chip when both match, so the composer doesn't
  // reserve room for a strip that ToolIntentSuggestions won't render.
  const showToolIntentBar = hasToolIntentSuggestions
    && !showWorkIntentBar
    && !isDesktopEditorOpen
    && !isCoworkerChat;
  const showSuggestionsBar = showFollowUpsBar || showToolIntentBar || showWorkIntentBar || showCollectionSuggestionBar;

  const fetchToolCatalog = useToolStore(state => state.fetchCatalog);
  const isFetchedToolCatalog = useToolStore(state => state.isFetchedCatalog);
  useEffect(() => {
    if (!isFetchedToolCatalog) {
      fetchToolCatalog().catch(() => {});
    }
  }, [id, isFetchedToolCatalog, fetchToolCatalog]);

  const agent = useMemo(() => {
    if (!chat?.agentId) return null;
    return getAgentById(chat.agentId);
  }, [chat?.agentId, getAgentById]);

  const isImageGeneration = agent?.outputTypes?.includes('image');

  useEffect(() => {
    setInitialLoadComplete(false);
  }, [id]);

  useEffect(() => {
    setShowCardExpirationWarning(false);
  }, [id]);

  // Set the chat input context when conversation changes
  const setContext = useChatInputStore(state => state.setContext);
  useEffect(() => {
    if (id) {
      setContext(id);
    }
  }, [id, setContext]);

  useEffect(() => {
    if (id && chat?.agentId && !isCoworkerChat) {
      useChatInputStore.getState().seedConversationAgent(id, chat.agentId);
    }
  }, [id, chat?.agentId, isCoworkerChat]);

  // Restore the conversation's last-used model in the composer once messages
  // are loaded. Input states are in-memory only, so after a refresh the model
  // selector falls back to the default text model / Auto even if the user was
  // chatting with e.g. GPT Image 2. Seeding is a no-op when the user already
  // made an explicit selection in this context (including explicit Auto).
  // Retry when models land in the store — addChats defers addModels, so the
  // first attempt often fails for image models that aren't in favorites yet.
  const historySeededIdRef = useRef(null);
  const chatMessageCount = chat?.messages?.length || 0;
  const modelStoreSize = useModelStore(state => Object.keys(state.models || {}).length);
  useEffect(() => {
    if (!id || chatMessageCount === 0) return;
    if (historySeededIdRef.current === id) return;
    const settled = useChatInputStore.getState().seedModelsFromConversationHistory(id);
    if (settled) {
      historySeededIdRef.current = id;
    }
  }, [id, chatMessageCount, modelStoreSize]);

  // User intent (sending, tapping scroll-to-bottom, or manually dragging) must
  // win over an in-flight cold-start scroll restore. On really long chats the
  // restore queued by useRestoreLastRoute stays active for several seconds —
  // it retries scrollToIndex and pages in archived history to reach the saved
  // mid-conversation anchor, and each retry calls clearBottomPin() +
  // scrollToIndex(anchor). That loop is what yanks the viewport back up to the
  // middle right after the user reaches (or sends to) the bottom. Dropping the
  // anchor pin and the pending scroll request for THIS conversation lets the
  // bottom-pin own the viewport. We deliberately leave the *persisted* anchor
  // alone: onViewableItemsChanged clears it once we settle at the bottom, while
  // a manual scroll-up keeps it so backgrounding mid-read still restores.
  const abortScrollRestore = useCallback(() => {
    scrollRequestRef.current?.request.cancel();
    if (scrollRequestTimerRef.current) clearTimeout(scrollRequestTimerRef.current);
    scrollRequestTimerRef.current = null;
    anchorPinRef.current?.archiveRequest?.cancel();
    anchorPinRef.current = null;
    const ui = useUIStore.getState();
    if (ui.pendingScrollRequest?.conversationId === idRef.current) {
      clearPendingScrollRequest();
    }
    if (ui.pendingScrollToTopRequest?.conversationId === idRef.current) {
      clearPendingScrollToTopRequest();
    }
  }, [clearPendingScrollRequest, clearPendingScrollToTopRequest]);

  const getModelById = useModelStore(state => state.getModelById);

  const findLockedSelectedModel = useCallback((modelIds) => {
    const ids = Array.isArray(modelIds) ? modelIds : [];
    for (const mid of ids) {
      const model = getModelById?.(mid) || useModelStore.getState().models?.[mid];
      if (model && isModelLockedForUser(user, model)) return model;
    }
    return null;
  }, [getModelById, user]);

  const requireAgentUpgrade = useCallback(() => {
    if (!agent?.flags?.isPlusOnly || isPlus) return false;
    openModal('PlusFeatureModal', { feature: 'bots' });
    return true;
  }, [agent?.flags?.isPlusOnly, isPlus, openModal]);

  const handleSend = useCallback(async (formattedMessage) => {
    if (onFocus) onFocus();

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'composer_send' });
      return false;
    }

    // Coworker thread: the composer routes to the task session — steer a
    // live run, or start a follow-up turn on an idle thread. Attachments
    // (formattedMessage.fileIds) are injected into the agent prompt as
    // fetch_url-able CDN URLs (validated server-side). coworkerSend inserts
    // the optimistic user bubble before the API call so the thread doesn't
    // sit empty until the agent replies. While live, Enter and Queue use
    // this path. Send now goes through handleSendNow.
    if (isCoworkerChat) {
      try {
        workQuestionReplyRef.current = null;
        abortScrollRestore();
        followLiveRef.current = false;
        if (coworker.isActive && !coworker.browserState?.question && !/^\/goal(?:\s|$)/i.test(String(formattedMessage?.text || '').trim())) {
          // Queueing a follow-up must not move the viewport off the reply
          // that is still streaming; the queue shows in the composer.
          const ok = coworkerEnqueue(formattedMessage?.text, formattedMessage?.fileIds);
          return ok;
        }
        const ok = await coworkerSend(formattedMessage?.text, formattedMessage?.fileIds);
        return ok;
      } catch (err) {
        reportWorkLaunchError(err, {
          source: 'work_thread_follow_up',
          fallback: 'Failed to send',
        });
        return false;
      }
    }

    // No connection gate here: emitSendUserMessage queues the message when
    // offline (bubble shows a waiting-for-connection state) and flushes on
    // reconnect — a send should never dead-end on a mobile network blip.

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'composer_send' });
      return false;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'composer_send' });
      return false;
    }

    if (requireAgentUpgrade()) return false;

    // The server checks the live balance; cached credits must not block a Chat send.

    const lockedModel = findLockedSelectedModel(formattedMessage?.modelIds);
    if (lockedModel) {
      openModelAccessPaywall(openModal, {
        model: lockedModel,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return false;
    }

    const emitOk = socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    if (emitOk === false) {
      // emitSendUserMessage already surfaced a toast and rolled back its
      // own optimistic state. Returning false tells ChatInput NOT to clear
      // the composer — the user's draft is preserved so they can retry.
      return false;
    }
    // Kill any in-flight cold-start restore so it can't drag us back to the
    // saved mid-conversation anchor, then proactively pin to the bottom the
    // instant the user sends — rather than waiting for the message to
    // round-trip through the socket + chat store. Without the abort, on long
    // chats the still-running restore re-pins to the middle right after we
    // scroll down ("thrown up high on send"). The store-driven pin (the
    // new-message effect) later reinforces this once the bubble arrives.
    // The pin does not arm follow-live: once the reply has text, tokens
    // grow in place.
    abortScrollRestore();
    followLiveRef.current = false;
    requestPinToBottomRef.current?.(true, 900, 5000);
    return true;
  }, [id, addToast, agent?.id, requireAgentUpgrade, isPlus, isGhost, isHeldForReview, needsEmailVerification, needsPhoneVerification, user, userId, openModal, onFocus, abortScrollRestore, findLockedSelectedModel, isCoworkerChat, coworker.isActive, coworker.browserState?.question, coworkerSend, coworkerEnqueue]);

  const handleSendNow = useCallback(async (formattedMessage) => {
    if (onFocus) onFocus();
    if (!isCoworkerChat) return handleSend(formattedMessage);
    try {
      workQuestionReplyRef.current = null;
      abortScrollRestore();
      followLiveRef.current = false;
      const ok = await coworkerSend(formattedMessage?.text, formattedMessage?.fileIds);
      return ok;
    } catch (err) {
      reportWorkLaunchError(err, { source: 'work_send_now', fallback: 'Failed to send' });
      return false;
    }
  }, [isCoworkerChat, handleSend, coworkerSend, abortScrollRestore, addToast, onFocus]);

  const handleQueuedFollowUpNow = useCallback(async (item) => {
    try {
      workQuestionReplyRef.current = null;
      abortScrollRestore();
      followLiveRef.current = false;
      const ok = await coworkerSendFollowUpNow(item);
      return ok;
    } catch (err) {
      reportWorkLaunchError(err, { source: 'work_send_now', fallback: 'Failed to send' });
      return false;
    }
  }, [coworkerSendFollowUpNow, abortScrollRestore, addToast]);

  const handleCancelInferencing = useCallback(async () => {
    // Coworker thread: "Stop" settles the task session (releases the credit
    // hold, revokes the session key) rather than cancelling an inference.
    if (isCoworkerChat) {
      try {
        await coworkerStop();
      } catch (err) {
        addToast(err?.message || 'Failed to stop', 'error');
      }
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    socketManager.emitCancelUserConversationInference(userId, id);
  }, [id, userId, isCoworkerChat, coworkerStop, addToast]);

  const handleFollowUpSuggestionPress = useCallback(async (suggestionText) => {
    if (!suggestionText?.trim()) return;

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'follow_up_suggestion' });
      return;
    }

    clearFollowUpSuggestions(id);

    // Work threads send through the task session, not the chat inference
    // socket — otherwise the suggestion would open a regular chat turn.
    // A pill click is a user message and must appear in the transcript
    // immediately (steer while live, new turn while idle).
    if (isCoworkerChat) {
      await handleSendNow({ text: suggestionText.trim(), fileIds: [] });
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'follow_up_suggestion' });
      return;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'follow_up_suggestion' });
      return;
    }

    if (requireAgentUpgrade()) return;

    // The server checks the live balance; cached credits must not block a Chat send.

    // Carry over the user's currently selected model/agent for this conversation
    // so follow-up suggestions respect the active model selector (e.g. a third-party
    // model) instead of falling back to the default Arya model on the backend.
    const chatInputState = useChatInputStore.getState();
    const inputState = chatInputState.inputStates?.[id];
    const selectedModelIds = Array.isArray(inputState?.selectedModelIds)
      ? inputState.selectedModelIds
      : [];
    const selectedAgentIds = Array.isArray(inputState?.selectedAgentIds)
      ? inputState.selectedAgentIds
      : [];
    const filterValidIds = chatInputState.filterValidIds;
    const modelIds = typeof filterValidIds === 'function' ? filterValidIds(selectedModelIds) : selectedModelIds;

    const lockedModel = findLockedSelectedModel(modelIds);
    if (lockedModel) {
      openModelAccessPaywall(openModal, {
        model: lockedModel,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return;
    }

    const formattedMessage = {
      text: suggestionText.trim(),
      modelIds,
      agentIds: typeof filterValidIds === 'function' ? filterValidIds(selectedAgentIds) : selectedAgentIds,
      actions: [],
      fileIds: [],
      parameters: {},
    };

    socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    // Same as handleSend: don't let an in-flight cold-start restore yank the
    // viewport back to the saved anchor; head straight to the bottom.
    abortScrollRestore();
    followLiveRef.current = false;
    requestPinToBottomRef.current?.(true, 900, 5000);
  }, [id, userId, isPlus, isGhost, isHeldForReview, needsEmailVerification, needsPhoneVerification, openModal, user, clearFollowUpSuggestions, abortScrollRestore, findLockedSelectedModel, isCoworkerChat, handleSendNow, requireAgentUpgrade]);

  // Scroll-to-bottom button animation
  useEffect(() => {
    if (showScrollButton) {
      setIsAnimatingOut((prev) => (prev ? false : prev));
      Animated.spring(scrollButtonAnimation, {
        toValue: 1,
        useNativeDriver: true,
        tension: 80,
        friction: 9,
      }).start();
    } else {
      setIsAnimatingOut((prev) => (prev ? prev : true));
      Animated.spring(scrollButtonAnimation, {
        toValue: 0,
        useNativeDriver: true,
        tension: 80,
        friction: 9,
      }).start(() => {
        setIsAnimatingOut((prev) => (prev ? false : prev));
      });
    }
  }, [showScrollButton, scrollButtonAnimation]);

  // Skip list auto-pin while the composer is catching up to a keyboard move.
  // After the IME settles, pin again if the reader was already on the tail
  // so Work/chat content slides up with the field instead of sitting under it.
  useChatKeyboardScroll({
    keyboardHeight,
    conversationId: id,
    animatingRef: keyboardAnimatingRef,
    getScrollState: () => {
      // Only once the list has real measurements and no restore anchor is
      // still positioning: isAtBottomRef starts true, and a pin here would
      // otherwise yank a cold-start restore away from its saved spot.
      const ui = useUIStore.getState();
      const hasPendingTarget = ui.pendingScrollRequest?.conversationId === idRef.current
        || ui.pendingScrollToTopRequest?.conversationId === idRef.current;
      const listMeasured = contentHeightRef.current > 0 && listLayoutHeightRef.current > 0;
      const shouldKeepTailVisible = listMeasured
        && !hasPendingTarget
        && !anchorPinRef.current
        && !isUserScrollingRef.current
        && Date.now() > userScrollIntentUntilRef.current
        && (isAtBottomRef.current || followLiveRef.current);
      return {
        canPin: shouldKeepTailVisible,
        interaction: userScrollIntentUntilRef.current,
        followLive: followLiveRef.current,
      };
    },
    onPin: (options) => requestPinToBottomRef.current?.(true, 400, 1400, options),
  });

  // Compute banner count for current conversation context
  const selectBannerCount = useCallback(
    (state) => (state.inputStates?.[id]?.banners?.length) || 0,
    [id]
  );
  const bannerCount = useChatInputStore(selectBannerCount);
  const extraPaddingBottom = bannerCount > 0 ? bannerCount * 50 : 0;

  const fallbackListPaddingBottom = (
    effectiveChatInputStyle === CHAT_INPUT_STYLE.FLOATING
      ? 160
      : 120
  ) + extraPaddingBottom
    + (showSuggestionsBar ? 80 : 0)
    + (chat?.inCollectionId ? 44 : 0)
    + ((isAuthenticated && isOwner) ? Math.max(keyboardHeight, insets.bottom) : 0);
  // Once the composer has mounted, its measured box already includes banners,
  // suggestions, keyboard lift, and safe-area padding. Prefer that single
  // source of truth so those features do not update list padding once from an
  // estimate and then again from ResizeObserver.
  // Keep short updates comfortably above the entire dock, including goals,
  // access cards and queued messages. Keep this gap after streaming settles
  // so the final reply does not jump down when its status changes.
  const workDockGap = isCoworkerChat ? (isXS ? 32 : 64) : 0;
  // On Mac, end the scroll viewport above the entire composer (including
  // follow-ups). Padding alone lets native scroll anchoring leave the final
  // receipt painted behind the dock when a suggestion arrives after settling.
  const reservesMacComposer = Platform.OS === 'macos' && !isDetailed && isAuthenticated && isOwner;
  const macComposerInset = reservesMacComposer
    ? (inputContainerHeight || fallbackListPaddingBottom)
    : 0;
  const listPaddingBottom = isDetailed
    ? 0
    : reservesMacComposer
      ? 12 + workDockGap
      : (Platform.OS === 'web' && isXS
      ? ((isAuthenticated && isOwner) && inputContainerHeight > 0
        ? inputContainerHeight + 8 + workDockGap
        : fallbackListPaddingBottom)
      : Math.max(
          fallbackListPaddingBottom,
          (isAuthenticated && isOwner) ? inputContainerHeight + workDockGap : 0
        ));

  const workScrollMetricsRef = useRef({});
  workScrollMetricsRef.current = { enabled: isCoworkerChat, bottomInset: listPaddingBottom, tailClearance: workDockGap };
  const workScrollFollower = useMemo(() => createWorkScrollFollower({
    getMetrics: () => {
      const el = getScrollElement();
      return {
        contentHeight: el?.scrollHeight ?? contentHeightRef.current,
        layoutHeight: el?.clientHeight ?? listLayoutHeightRef.current,
        offset: el?.scrollTop ?? scrollOffsetRef.current,
        bottomInset: workScrollMetricsRef.current.bottomInset,
        tailClearance: workScrollMetricsRef.current.tailClearance,
        blocked: !workScrollMetricsRef.current.enabled || !!anchorPinRef.current
          || isUserScrollingRef.current,
      };
    },
    scrollTo: (offset) => {
      const el = getScrollElement();
      scrollOffsetRef.current = offset;
      if (el) el.scrollTop = offset;
      else flatListRef.current?.scrollToOffset?.({ offset, animated: false });
    },
    requestFrame: (callback) => requestAnimationFrame(callback),
    cancelFrame: (frame) => cancelAnimationFrame(frame),
  // A conversation change starts a fresh following session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [id, getScrollElement]);
  useEffect(() => () => workScrollFollower.dispose(), [workScrollFollower]);
  const handleWorkBodyLayout = useCallback((messageId, height) => {
    workScrollFollower.measureReply(messageId, height);
  }, [workScrollFollower]);

  // FlatList contentContainerStyle must stay referentially stable while the
  // user scrolls. Bundling it with scroll-chrome state (showScrollButton,
  // isNearTop, button animation) used to recreate this object on every
  // near-bottom / near-top threshold cross — on mobile Chrome that re-applied
  // contentContainerStyle mid-gesture and made the list jump while reading.
  // The transparent native header already includes the top safe area. Use its
  // measured height so the first date divider sits just below the controls.
  const nativeOverlayHeaderPaddingTop = (Platform.OS === 'ios' || Platform.OS === 'android') && !isDetailed
    ? navigationHeaderHeight
    : 0;
  // A short right-aligned first bubble sits directly under the floating
  // glass toolbar (and its shadow), so give it extra clearance there.
  const floatingToolbarClearance = !isDetailed && (Platform.OS === 'ios' || Platform.OS === 'android' || (Platform.OS === 'web' && isXS))
    ? 14
    : 0;
  const listPaddingTop = (isXS || isCompact ? 15 : 20) + nativeOverlayHeaderPaddingTop + floatingToolbarClearance;
  const listContentContainerStyle = useMemo(() => ({
    paddingHorizontal: 0,
    paddingTop: listPaddingTop,
    paddingBottom: listPaddingBottom,
    ...(Platform.OS === 'web' && {
      overscrollBehavior: 'contain',
      // Browser anchoring can follow a footer as the reply above it grows.
      // The explicit follower owns Work scrolling; the browser must not
      // choose an anchor that fights it when tool rows fold or mount.
      overflowAnchor: 'none',
    }),
  }), [listPaddingTop, listPaddingBottom]);

  // Dynamic styles (chrome / chrome-adjacent — safe to churn during scroll)
  const styles = useMemo(() => ({
    ...baseStyles,
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: isNearTop && !isDetailed ? 0 : 1,
      borderTopColor: isNearTop && !isDetailed ? 'transparent' : colors.border_color_secondary,
    },
    mainContent: {
      flex: 0,
      position: 'absolute',
      ...(Platform.OS === 'web' && { minHeight: 0 }),
      top: 0,
      left: 0,
      right: 0,
      bottom: isAuthenticated ? 0 : 80,
      flexDirection: 'column',
      ...(Platform.OS === 'web' && { display: 'flex', transition: 'right 0.3s ease-in-out' }),
    },
    lockColumn: {
      width: '100%',
      paddingHorizontal: isXS || isCompact ? 12 : 20,
    },
    lockColumnInner: {
      width: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    list: listContentContainerStyle,
    inputContainer: {
      width: isXS ? 'auto' : '100%',
      ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
      paddingHorizontal: 0,
      alignItems: 'center',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      // Work keeps the guide and composer in this overlay. A glass
      // background lets the transcript paint through both, which reads as
      // the output sitting behind the dock. Regular chat still blurs.
      backgroundColor: (isXS || isCoworkerChat) ? colors.solid_color_primary : 'transparent',
      paddingBottom: isAuthenticated && isOwner
        ? (Platform.OS === 'web' ? keyboardHeight : Math.max(keyboardHeight, insets.bottom))
        : 0,
    },
    composerStack: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      zIndex: 11,
    },
    composerGate: {
      maxHeight: Platform.OS === 'web'
        ? '40vh'
        // Android adjustResize has already removed the IME from windowHeight;
        // subtracting keyboardHeight again makes the card unusably short.
        : Math.max(120, Math.min(300, (
          Platform.OS === 'android'
            ? windowHeight
            : windowHeight - Math.max(keyboardHeight, insets.bottom)
        ) - 240)),
      overflow: 'hidden',
      minHeight: 0,
      marginBottom: 8,
      width: '100%',
    },
    scrollToBottomButtonContainer: {
      position: 'absolute',
      top: -44,
      alignSelf: 'center',
      zIndex: 20,
      opacity: scrollButtonAnimation,
      transform: [{
        translateY: scrollButtonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [20, 0],
        })
      }, {
        scale: scrollButtonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0.5, 1],
        })
      }],
      pointerEvents: (!showScrollButton && !isAnimatingOut) ? 'none' : 'auto',
    },
    scrollToBottomButton: {
      backgroundColor: themeStyles.backgroundColor,
      borderColor: themeStyles.borderColor,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(18px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
      } : {}),
      borderWidth: 1,
    },
    scrollToBottomLiveText: {
      fontSize: 13,
      maxWidth: 260,
    },
    chatInputDetailedContainer: {
      zIndex: 10,
      height: '100%',
      backgroundColor: 'transparent',
    },
    archiveLoaderContainer: {
      width: '100%',
      alignItems: 'center',
      paddingTop: 2,
      paddingBottom: 12,
    },
    archiveLoaderButton: {
      backgroundColor: colors.solid_color_block,
      borderColor: colors.border_color_secondary,
      borderWidth: 1,
    },
  }), [isXS, isCompact, isCoworkerChat, colors, isAuthenticated, scrollButtonAnimation, showScrollButton, isAnimatingOut, themeStyles.backgroundColor, themeStyles.borderColor, keyboardHeight, windowHeight, isOwner, isNearTop, isDetailed, insets.bottom, listContentContainerStyle]);

  // Socket initialization / mark as read
  useEffect(() => {
    if (isAuthenticated) {
      if (!isCompact) {
        setCurrentLibraryId(id);
      }

      // Only the owner can mark a conversation read — shared/public chats
      // viewed by other users 404 on the /read endpoint and spam the console.
      if (isOwner) {
        useChatStore.getState().markChatAsRead(id);
      }

      return () => {
        if (!isCompact) {
          setCurrentLibraryId(null);
        }
      };
    }
  }, [id, isAuthenticated, isCompact, isOwner]);

  const pinStabilizeMsRef = useRef(0);

  const androidChatScroller = useMemo(() => createAndroidChatScroller({
    getList: () => flatListRef.current,
    getMetrics: () => ({
      contentHeight: contentHeightRef.current,
      layoutHeight: listLayoutHeightRef.current,
      offset: scrollOffsetRef.current,
      blocked: isUserScrollingRef.current || !!anchorPinRef.current
        || shouldHoldChatScroll({ ...chatScrollStateRef.current, followLive: followLiveRef.current }),
    }),
  }), []);

  const clearBottomRepins = useCallback(() => {
    const handles = bottomRepinHandlesRef.current;
    bottomRepinHandlesRef.current = [];
    for (const h of handles) {
      if (h?.type === 'raf') {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(h.id);
      } else {
        clearTimeout(h.id);
      }
    }
  }, []);

  const clearBottomPin = useCallback(() => {
    androidChatScroller.cancel();
    wantsBottomRef.current = false;
    pinStabilizeMsRef.current = 0;
    if (bottomStabilizeTimerRef.current) {
      clearTimeout(bottomStabilizeTimerRef.current);
      bottomStabilizeTimerRef.current = null;
    }
    if (bottomMaxTimerRef.current) {
      clearTimeout(bottomMaxTimerRef.current);
      bottomMaxTimerRef.current = null;
    }
    clearBottomRepins();
  }, [clearBottomRepins, androidChatScroller]);

  // Native drag callbacks do not reliably fire for Firefox wheel/touchpad input
  // (or Android Chrome touch scrolling through RN Web's FlatList). Cancel an
  // active programmatic pin at the input event — in capture phase so we win
  // even when the scrollable child stops bubbling — before a scheduled re-pin
  // can erase the user's smaller incremental scroll deltas.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const node = messagesContainerRef.current;
    if (!node?.addEventListener) return undefined;

    const handleDirectScrollIntent = (event) => {
      // onScrollBeginDrag doesn't fire for web wheel/trackpad input, so this
      // is the only signal that marks genuine user scroll intent on web.
      hasUserDraggedRef.current = true;
      userScrollIntentUntilRef.current = Date.now() + 1000;
      workScrollFollower.pause();
      if (event?.type === 'touchstart') isUserScrollingRef.current = true;
      // Taking over the transcript also cancels live following, before the
      // browser reports a new offset. Otherwise touch/scrollbar/keyboard input
      // can release the current pin but a delayed suggestion or layout update
      // immediately starts another. Normal chat resumes via the down-arrow;
      // Work resumes through its own deliberate-scroll-to-tail check.
      followLiveRef.current = false;
      if (event?.type === 'wheel' && event.deltaY > 0) {
        lastDownwardScrollAtRef.current = Date.now();
      }
      if (wantsBottomRef.current) clearBottomPin();
    };
    const handleWheelOrTouchScrollIntent = (event) => {
      handleDirectScrollIntent(event);
      abortScrollRestore();
      // The veiled list has pointer-events off, so this container listener is
      // the only signal that the user wants to scroll during positioning.
      // Reveal rather than making them wait out the settle.
      endListSettleVeilRef.current?.();
    };
    // A touch scroll on mobile web can run well past the 1s intent window
    // (a long drag, then momentum). Keep the window fresh while the finger
    // moves and give momentum a little longer after it lifts, so scrolling
    // back down to the tail still re-arms follow-live.
    const handleTouchMoveIntent = () => {
      userScrollIntentUntilRef.current = Date.now() + 1000;
    };
    const handleTouchEndIntent = () => {
      isUserScrollingRef.current = false;
      userScrollIntentUntilRef.current = Date.now() + 1500;
      workScrollFollower.schedule();
    };
    const handleKeyScrollIntent = (event) => {
      if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
      if (event.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      handleWheelOrTouchScrollIntent(event);
    };
    const opts = { passive: true, capture: true };
    node.addEventListener('wheel', handleWheelOrTouchScrollIntent, opts);
    node.addEventListener('keydown', handleKeyScrollIntent, opts);
    node.addEventListener('touchstart', handleWheelOrTouchScrollIntent, opts);
    node.addEventListener('touchmove', handleTouchMoveIntent, opts);
    node.addEventListener('touchend', handleTouchEndIntent, opts);
    node.addEventListener('touchcancel', handleTouchEndIntent, opts);
    // pointerdown cancels an active bottom-pin (clicking the list means the
    // user took over) but must NOT abort an explicit jump-to-message. Closing
    // search lands mouseup on the chat underneath and would otherwise drop
    // the pending scroll before ChatView can honor it.
    node.addEventListener('pointerdown', handleDirectScrollIntent, opts);

    return () => {
      node.removeEventListener('wheel', handleWheelOrTouchScrollIntent, opts);
      node.removeEventListener('keydown', handleKeyScrollIntent, opts);
      node.removeEventListener('touchstart', handleWheelOrTouchScrollIntent, opts);
      node.removeEventListener('touchmove', handleTouchMoveIntent, opts);
      node.removeEventListener('touchend', handleTouchEndIntent, opts);
      node.removeEventListener('touchcancel', handleTouchEndIntent, opts);
      node.removeEventListener('pointerdown', handleDirectScrollIntent, opts);
    };
  }, [clearBottomPin, abortScrollRestore, isLoading, error, workScrollFollower]);

  const recoverLinuxChatScroll = useCallback(() => {
    if (!isLinuxChromiumWeb || isCoworkerChat) return false;
    const ui = useUIStore.getState();
    return linuxScrollRecovery.observe(getScrollElement(), {
      blocked: wantsBottomRef.current || !!anchorPinRef.current || !!prependCompensationRef.current
        || isUserScrollingRef.current || Date.now() <= userScrollIntentUntilRef.current
        || ui.pendingScrollRequest?.conversationId === id
        || ui.pendingScrollToTopRequest?.conversationId === id,
    });
  }, [isLinuxChromiumWeb, isCoworkerChat, linuxScrollRecovery, getScrollElement, id]);

  useEffect(() => {
    if (!isLinuxChromiumWeb || isCoworkerChat || isLoading || error) return undefined;
    return linuxScrollRecovery.listen(messagesContainerRef.current);
  }, [isLinuxChromiumWeb, isCoworkerChat, isLoading, error, linuxScrollRecovery]);

  // Holds the reader's top row still through layout changes above it. Pins,
  // restores, prepends, jumps, Studio, and Work own the viewport while
  // active, so the anchor only re-measures during those.
  const syncChatScrollAnchor = useCallback((el) => {
    if (Platform.OS !== 'web' || !el) return 0;
    const now = Date.now();
    // The raw DOM listener sees every scroll frame, including touch momentum
    // between RN Web's throttled onScroll callbacks. A pending archive load
    // must preserve that movement when its rows arrive.
    trackChatPrependScroll(prependCompensationRef.current, {
      offset: el.scrollTop, contentHeight: el.scrollHeight, element: el, now,
    });
    if (isCoworkerChat) return 0;
    const ui = useUIStore.getState();
    const owned = isVideoStudioOpenRef.current
      || wantsBottomRef.current
      || followLiveRef.current
      || (!!anchorPinRef.current && now <= anchorPinRef.current.until)
      || (!!prependCompensationRef.current && now <= prependCompensationRef.current.until)
      || ui.pendingScrollRequest?.conversationId === id
      || ui.pendingScrollToTopRequest?.conversationId === id;
    return chatScrollAnchor.sync(el, { adjust: !owned });
  }, [isCoworkerChat, chatScrollAnchor, id]);

  // RN Web measures content size in a timer after paint and emits onScroll
  // only when a continuous scroll starts and ends, so corrections made from
  // those callbacks land a frame after the jump is already visible. Observe
  // the DOM directly: ResizeObserver runs after layout but before paint.
  useEffect(() => {
    if (Platform.OS !== 'web' || isCoworkerChat || isLoading || error) return undefined;
    const el = getScrollElement();
    const content = el?.firstElementChild;
    if (!content || typeof ResizeObserver === 'undefined') return undefined;
    const sync = () => { syncChatScrollAnchor(el); };
    const observer = new ResizeObserver(sync);
    observer.observe(content);
    el.addEventListener('scroll', sync, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', sync);
    };
  }, [isCoworkerChat, isLoading, error, initialLoadComplete, getScrollElement, syncChatScrollAnchor]);

  const restoreStudioScrollIfSnapped = useCallback((nextOffset, el) => {
    if (!isVideoStudioOpenRef.current) return false;
    const previousOffset = scrollOffsetRef.current;
    if (!shouldRestoreStudioScroll({
      previousOffset,
      nextOffset,
      userScrolling: isUserScrollingRef.current,
      userIntentUntil: userScrollIntentUntilRef.current,
      lastDownwardAt: lastDownwardScrollAtRef.current,
      pinningToBottom: wantsBottomRef.current,
    })) return false;
    if (el) el.scrollTop = previousOffset;
    try {
      flatListRef.current?.scrollToOffset?.({ offset: previousOffset, animated: false });
    } catch (_) { /* list may be mid-remount */ }
    return true;
  }, []);

  const pinToBottomNow = useCallback((animated = false) => {
    if (shouldHoldChatScroll({ ...chatScrollStateRef.current, followLive: followLiveRef.current })) {
      clearBottomPin();
      return;
    }
    if (Platform.OS === 'android') {
      androidChatScroller.scrollToBottom();
      return;
    }
    if (Platform.OS === 'web') {
      const el = getScrollElement();
      if (el) {
        // RN Web replaces el.scrollTo with its {x, y, animated} API. Passing
        // DOM options to that override sends y=0, so delayed reply/follow-up
        // pins pull the reader to the top. Call the browser method directly.
        const nativeScrollTo = el.ownerDocument?.defaultView?.Element?.prototype.scrollTo;
        if (animated && typeof nativeScrollTo === 'function') {
          nativeScrollTo.call(el, { top: el.scrollHeight, behavior: 'smooth' });
        } else {
          el.scrollTop = el.scrollHeight;
        }
        return;
      }
    }
    if (!flatListRef.current) return;
    try {
      if (flatListRef.current.scrollToOffset) {
        flatListRef.current.scrollToOffset({ offset: 999999, animated });
      } else if (flatListRef.current.scrollToEnd) {
        flatListRef.current.scrollToEnd({ animated });
      }
    } catch (error) {
      console.warn('Failed to pin to bottom:', error);
    }
  }, [getScrollElement, clearBottomPin, androidChatScroller]);

  // Re-assert the bottom across the next few frames so the pin survives the
  // async MVC-removal render and lands after each layout pass. Each attempt is
  // a no-op once the user takes over (wantsBottomRef cleared) or the list is
  // gone. Cheap: a handful of scrollToOffset calls over ~600ms.
  const scheduleBottomRepins = useCallback(() => {
    clearBottomRepins();
    const fire = () => {
      if (!wantsBottomRef.current || !flatListRef.current) return;
      pinToBottomNow(false);
    };
    if (typeof requestAnimationFrame === 'function') {
      bottomRepinHandlesRef.current.push({ type: 'raf', id: requestAnimationFrame(fire) });
    }
    for (const delay of [50, 150, 300, 600]) {
      bottomRepinHandlesRef.current.push({ type: 'timeout', id: setTimeout(fire, delay) });
    }
  }, [clearBottomRepins, pinToBottomNow]);

  // Restart the debounced stabilize timer. Called from onContentSizeChange so the
  // pin stays active as long as content keeps growing, and releases shortly after
  // layout quiets down.
  const bumpStabilizeTimer = useCallback(() => {
    if (!wantsBottomRef.current) return;
    const ms = pinStabilizeMsRef.current;
    if (!ms) return;
    if (bottomStabilizeTimerRef.current) clearTimeout(bottomStabilizeTimerRef.current);
    bottomStabilizeTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomStabilizeTimerRef.current = null;
    }, ms);
  }, []);

  // Request the list to stay pinned at the bottom through any pending content-size
  // growth. The pin is released when content has been quiet for `stabilizeMs`
  // (debounced — resets on every content-size change), after `maxMs` as a hard
  // cap, or immediately on user interaction. It is a one-off positioning
  // move: following the live tail afterwards is opt-in via `followLive`
  // (the down-arrow), so a send or an opened thread never chases tokens.
  const requestPinToBottom = useCallback((animated = false, stabilizeMs = 600, maxMs = 4000, { followLive = false } = {}) => {
    // This newer positioning request owns the viewport even if the archive
    // response arrives before its scroll event. Manual scrolling keeps the
    // pending prepend baseline; an explicit jump/send replaces it.
    prependCompensationRef.current = null;
    anchorPinRef.current?.archiveRequest?.cancel();
    if (workScrollMetricsRef.current.enabled) {
      followLiveRef.current = false;
      clearBottomPin();
      anchorPinRef.current = null;
      // Only initial positioning / an explicit jump is immediate. An animated
      // send uses the same single animation as subsequent activity.
      if (!animated) {
        pinToBottomNow(false);
        workScrollFollower.positionAtTail({ stabilizeMs, maxMs });
      } else {
        workScrollFollower.resume();
      }
      return;
    }
    followLiveRef.current = !!followLive;
    wantsBottomRef.current = true;
    pinStabilizeMsRef.current = stabilizeMs;
    // A bottom-pin and a restore anchor-pin are mutually exclusive — if both
    // are active, onContentSizeChange's `wantsBottomRef`-first check keeps the
    // bottom-pin winning, but residual anchor scrollToIndex corrections can
    // still fight the bottom-pin's scrollToOffset,
    // producing the "jumps to the middle on send" symptom on long chats that
    // were cold-restored to a saved anchor. Clear the anchor up front so the
    // bottom-pin owns the viewport cleanly.
    anchorPinRef.current = null;
    // A new explicit request may legitimately resend the same Android target:
    // the previous cycle's command can have been dropped before layout.
    androidChatScroller.cancel();
    pinToBottomNow(animated);
    // Defeat the iOS MVC race + cover the pre-message-arrival window by
    // re-firing the scroll across the next few frames.
    scheduleBottomRepins();
    if (bottomStabilizeTimerRef.current) clearTimeout(bottomStabilizeTimerRef.current);
    if (bottomMaxTimerRef.current) clearTimeout(bottomMaxTimerRef.current);
    bottomStabilizeTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomStabilizeTimerRef.current = null;
    }, stabilizeMs);
    bottomMaxTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomMaxTimerRef.current = null;
    }, maxMs);
  }, [pinToBottomNow, scheduleBottomRepins, clearBottomPin, workScrollFollower, androidChatScroller]);
  // Expose the latest requestPinToBottom to earlier-defined handlers (handleSend)
  // via a stable ref, sidestepping the const temporal-dead-zone.
  requestPinToBottomRef.current = requestPinToBottom;

  // Reveal the list at its (now stable) position.
  const endListSettleVeil = useCallback(() => {
    if (settleVeilQuietTimerRef.current) {
      clearTimeout(settleVeilQuietTimerRef.current);
      settleVeilQuietTimerRef.current = null;
    }
    if (settleVeilMaxTimerRef.current) {
      clearTimeout(settleVeilMaxTimerRef.current);
      settleVeilMaxTimerRef.current = null;
    }
    if (!settleVeilActiveRef.current) return;
    settleVeilActiveRef.current = false;
    Animated.timing(listSettleOpacity, {
      toValue: 1,
      duration: 220,
      useNativeDriver: true,
    }).start(() => {
      // Unmount the skeleton only after the cross-fade completes.
      setIsSettleVeilUp(false);
    });
  }, [listSettleOpacity]);
  useEffect(() => {
    endListSettleVeilRef.current = endListSettleVeil;
  }, [endListSettleVeil]);

  // Debounced "content has gone quiet" detector; restarted on every
  // content-size change while the veil is up.
  const bumpListSettleVeil = useCallback(() => {
    if (!settleVeilActiveRef.current) return;
    if (settleVeilQuietTimerRef.current) clearTimeout(settleVeilQuietTimerRef.current);
    settleVeilQuietTimerRef.current = setTimeout(() => {
      // Live runs hold the veil until the first event snapshot so tool
      // trails do not pop in after reveal. Idle history already has the
      // conversation — do not wait on the orchestrator.
      if (settleVeilActiveRef.current && !workEventsReadyRef.current) {
        bumpListSettleVeil();
        return;
      }
      endListSettleVeil();
    }, 280);
  }, [endListSettleVeil]);

  const beginListSettleVeil = useCallback((maxMs = 2200, options) => {
    // Regular web chats settle without the native measurement shake. Work
    // threads do not: they render from the top, then grow as tool trails and
    // the event snapshot land. Veil those so the user only sees the tail.
    if (Platform.OS === 'web' && !options?.allowWeb) return;
    settleVeilActiveRef.current = true;
    listSettleOpacity.setValue(0);
    setIsSettleVeilUp(true);
    // Safety: if no content-size events arrive at all (e.g. tiny chat already
    // measured), reveal quickly rather than waiting for the hard cap. Work
    // still waits on the event snapshot via bumpListSettleVeil.
    bumpListSettleVeil();
    if (settleVeilMaxTimerRef.current) clearTimeout(settleVeilMaxTimerRef.current);
    settleVeilMaxTimerRef.current = setTimeout(endListSettleVeil, maxMs);
  }, [bumpListSettleVeil, endListSettleVeil, listSettleOpacity]);

  // Clear timers on unmount
  useEffect(() => () => {
    scrollRequestRef.current?.request.cancel();
    prependCompensationRef.current = null;
    anchorPinRef.current?.archiveRequest?.cancel();
    anchorPinRef.current = null;
    if (scrollRequestTimerRef.current) clearTimeout(scrollRequestTimerRef.current);
    clearBottomPin();
    if (settleVeilQuietTimerRef.current) clearTimeout(settleVeilQuietTimerRef.current);
    if (settleVeilMaxTimerRef.current) clearTimeout(settleVeilMaxTimerRef.current);
    if (showScrollButtonRafRef.current && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(showScrollButtonRafRef.current);
      showScrollButtonRafRef.current = 0;
    }
  }, [clearBottomPin]);

  // Reset the per-conversation initial-scroll latch when the chat changes.
  useEffect(() => {
    scrollRequestRef.current?.request.cancel();
    scrollRequestRef.current = null;
    if (scrollRequestTimerRef.current) clearTimeout(scrollRequestTimerRef.current);
    scrollRequestTimerRef.current = null;
    initialScrolledIdRef.current = null;
    lastMessageId.current = null;
    studioUserTurnRef.current = null;
    prependCompensationRef.current = null;
    anchorPinRef.current?.archiveRequest?.cancel();
    anchorPinRef.current = null;
    hasUserDraggedRef.current = false;
    isUserScrollingRef.current = false;
    userScrollIntentUntilRef.current = 0;
    followLiveRef.current = false;
    clearBottomPin();
    workQuestionReplyRef.current = null;
    // Drop any settle veil left over from the previous conversation —
    // otherwise switching from a long chat (veil up, list at opacity 0) to a
    // short one (<15 messages, which never starts a new veil) leaves the new
    // chat invisible until the previous chat's stale timers fire. Reset
    // instantly rather than fading: a fresh veil is started right after for
    // long chats by the initial-load effect below.
    if (settleVeilQuietTimerRef.current) {
      clearTimeout(settleVeilQuietTimerRef.current);
      settleVeilQuietTimerRef.current = null;
    }
    if (settleVeilMaxTimerRef.current) {
      clearTimeout(settleVeilMaxTimerRef.current);
      settleVeilMaxTimerRef.current = null;
    }
    if (settleVeilActiveRef.current) {
      settleVeilActiveRef.current = false;
      listSettleOpacity.stopAnimation();
      listSettleOpacity.setValue(1);
      setIsSettleVeilUp(false);
    }
  }, [id, clearBottomPin, listSettleOpacity]);

  // Scroll to bottom on initial load (skip if a scroll-to-message request is pending for this chat).
  // Fires once per conversation id so new incoming messages don't yank an
  // attentive reader back to the bottom.
  useEffect(() => {
    if (!chat?.messages?.length) return;
    if (initialScrolledIdRef.current === id) return;
    const pending = useUIStore.getState().pendingScrollRequest;
    if (pending && pending.conversationId === id) {
      initialScrolledIdRef.current = id;
      return;
    }
    // If the conversation is mid-inference the first time we encounter it,
    // latch WITHOUT pinning. A brand-new chat is navigated into with
    // isInferencing already set (see app.jsx), so the only initial-load pin
    // that could fire here is a *deferred* one — it would run the instant
    // generation finishes and yank the user to the very bottom right as they
    // begin reading the first response. Latching neutralizes that. Existing
    // chats are opened while idle and fall through to the normal pin below.
    // Work threads are the opposite: the live tail *is* the thing to watch,
    // and isInferencing stays true for the whole run.
    if ((isInferencing || isInferencingWaiting) && !isCoworkerChat) {
      initialScrolledIdRef.current = id;
      return;
    }
    initialScrolledIdRef.current = id;
    // Hide the settling: position the list while it's veiled and fade in once
    // layout is quiet, so the user never sees the measurement shake. Short
    // chats measure in a frame or two and don't shake — skip the veil so they
    // keep appearing instantly. Web Work can paint the saved transcript
    // while tools attach; a live stream never reliably goes quiet, so a
    // settle veil here just adds a delay to every history open.
    const messageCount = chat?.messages?.length || 0;
    if (Platform.OS !== 'web' && isCoworkerChat && coworker.isActive && messageCount >= 6) {
      // Short hard cap: a live run's transcript never goes quiet, and holding
      // a skeleton over streaming work is worse than showing it settle.
      beginListSettleVeil(1600, { allowWeb: true });
    } else if (!isCoworkerChat && messageCount >= 15) {
      beginListSettleVeil();
    }
    // Use an extra-long pin window for initial load because long chats may take
    // time to finish rendering markdown, code, math, and images, all of which
    // grow the content height asynchronously after first paint.
    // Work resumes its bounded activity follower after positioning history;
    // normal chat uses a temporary pin while its rows finish measuring.
    requestPinToBottom(false, isCoworkerChat ? 1000 : 800, isCoworkerChat ? 8000 : 6000);
  }, [id, chat?.messages, isInferencing, isInferencingWaiting, isCoworkerChat, coworker.isActive, requestPinToBottom, beginListSettleVeil]);

  // Event snapshot can land after the last content-size change. Nudge the
  // veil so it can reveal once trails have attached.
  useEffect(() => {
    if (!isCoworkerChat) return;
    if (!coworkerHasSnapshot && !(coworker.events?.length > 0)) return;
    bumpListSettleVeil();
  }, [isCoworkerChat, coworkerHasSnapshot, coworker.events?.length, bumpListSettleVeil]);

  // Auto-scroll during voice mode. Hands-free, so the transcript follows the
  // conversation on desktop; mobile keeps its reading position.
  useEffect(() => {
    if (!isVoiceModeActive || !flatListRef.current || !chat?.messages?.length) return;
    if (keyboardAnimatingRef.current) return;
    requestPinToBottom(true, 500, 2500, { followLive: !mobileReadingChat });
  }, [isVoiceModeActive, chat?.messages?.length, requestPinToBottom, mobileReadingChat]);

  // Keep the tail in view when follow-up suggestions land — but only for a
  // reader who explicitly followed the live tail. A finished answer must not
  // move the viewport for anyone reading it from the top.
  useEffect(() => {
    if (!flatListRef.current) return;
    if (isInferencing || isInferencingWaiting) return;

    const hasFollowUps = followUpSuggestions?.suggestions?.length > 0;

    if (hasFollowUps) {
      const suggestionScrollTimer = setTimeout(() => {
        if (!flatListRef.current) return;
        if (!followLiveRef.current) return;
        const layoutH = listLayoutHeightRef.current;
        const contentH = contentHeightRef.current;
        const offset = scrollOffsetRef.current;
        // Tight threshold: a loose 100px window was re-pinning users who had
        // already scrolled up a bit to read the answer when follow-ups landed.
        const actuallyAtBottom = layoutH > 0 && contentH > 0
          ? offset + layoutH >= contentH - 24
          : isAtBottomRef.current;

        if (actuallyAtBottom) {
          requestPinToBottom(true, 500, 2000, { followLive: true });
        }
      }, 300);
      return () => clearTimeout(suggestionScrollTimer);
    }
  }, [followUpSuggestions?.suggestions?.length, isInferencing, isInferencingWaiting, requestPinToBottom]);

  // Debug logging
  useEffect(() => {
    if (__DEV__) {
      console.log('[ChatView] Chat data updated:', {
        chatId: chat?.id,
        messageCount: chat?.messages?.length,
        chatExists: !!chat,
      });
    }
  }, [chat, chat?.messages?.length]);

  // Memoize chat data. Streaming flushes change only message content — never
  // order — so cache the sort by a structure signature (message keys + hidden
  // flags) and re-run the O(n log n) sort + date parses only when the
  // structure actually changes. Between structure changes the previous
  // ordering is replayed with the fresh message objects.
  const chatSortCacheRef = useRef({ signature: null, visibleKeys: [] });
  // Work threads render the projected transcript (server rows + live tail);
  // the chat store only holds the server projection for them.
  const workTranscript = coworker.transcript;
  const sortCacheConversationIdRef = useRef(id);
  // Native Drawer reuses this ChatView instance across /c/:id params.
  // Reset during render (not in an effect) so this pass cannot replay the
  // previous conversation's keys.
  if (sortCacheConversationIdRef.current !== id) {
    sortCacheConversationIdRef.current = id;
    chatSortCacheRef.current = { signature: null, visibleKeys: [] };
  }
  const chatData = useMemo(() => {
    // An empty projection ([] is truthy) must not shadow server rows: fall back
    // to the stored conversation when the Work transcript has no rows.
    const hasWorkTranscript = Array.isArray(workTranscript) && workTranscript.length > 0;
    const isWork = isCoworkerChat && hasWorkTranscript;
    const source = isWork ? workTranscript : (chat?.messages || []);
    if (isWork) {
      // Work transcript rows are already projected in exact chronological and
      // sequence order (server messages, event tail, live draft, pending sends,
      // queued follow-ups). Do NOT re-sort by timestamp — clock skew or
      // synthetic timestamps would scramble the message flow.
      const visible = [];
      let signature = '';
      for (let i = 0; i < source.length; i++) {
        const m = source[i];
        if (m.isHidden) continue;
        visible.push(m);
        signature += (m._stableKey || m.id || m._id || `idx-${i}`) + '|';
      }
      return {
        messages: visible,
        title: chatTitle,
        isArchived: chat?.isArchived,
        sortSignature: signature,
        visibleKeys: visible.map(m => m._stableKey || m.id || m._id),
      };
    }

    const keys = new Array(source.length);
    let signature = '';
    for (let i = 0; i < source.length; i++) {
      const m = source[i];
      const key = m._stableKey || m.id || m._id || `idx-${i}`;
      keys[i] = key;
      signature += key + (m.isHidden ? '1' : '0') + '|';
    }

    let sorted;
    let visibleKeys;
    const cache = chatSortCacheRef.current;
    let usedCache = false;
    if (cache.signature === signature) {
      const byKey = new Map();
      for (let i = 0; i < source.length; i++) byKey.set(keys[i], source[i]);
      const replayed = new Array(cache.visibleKeys.length);
      let cacheHit = true;
      for (let i = 0; i < cache.visibleKeys.length; i++) {
        const msg = byKey.get(cache.visibleKeys[i]);
        if (!msg) {
          cacheHit = false;
          break;
        }
        replayed[i] = msg;
      }
      if (cacheHit) {
        sorted = replayed;
        visibleKeys = cache.visibleKeys;
        usedCache = true;
      }
    }
    if (!usedCache) {
      const decorated = [];
      for (let i = 0; i < source.length; i++) {
        const m = source[i];
        if (m.isHidden) continue;
        decorated.push([new Date(m.timestamp || m.date || 0).getTime(), m, keys[i]]);
      }
      // Array.prototype.sort is stable, so ties keep their original order —
      // identical behavior to the previous comparator-based sort.
      decorated.sort((a, b) => a[0] - b[0]);
      sorted = decorated.map(entry => entry[1]);
      visibleKeys = decorated.map(entry => entry[2]);
    }

    return {
      messages: sorted,
      title: chatTitle,
      isArchived: chat?.isArchived,
      sortSignature: signature,
      visibleKeys,
    };
  }, [chat?.messages, isCoworkerChat, workTranscript, chatTitle, chat?.isArchived]);

  // Layout callbacks and scheduled pins must see the response in the same
  // render, before effects run, so the first token cannot move the viewport.
  chatScrollStateRef.current = {
    isCoworkerChat,
    isInferencing: !!(isInferencing || isInferencingWaiting),
    hasAssistantResponse: chatData.messages[chatData.messages.length - 1]?.role === 'assistant',
  };

  // Persist the sort cache after commit — mutating a ref during render would
  // leak work from renders React discards.
  useEffect(() => {
    chatSortCacheRef.current = {
      signature: chatData.sortSignature,
      visibleKeys: chatData.visibleKeys,
    };
  }, [chatData]);

  // Sending should always reveal the user's own bubble, even in very long
  // conversations where FlatList/content measurement settles over multiple
  // frames.
  useEffect(() => {
    const latestMessage = chatData.messages[chatData.messages.length - 1];
    const latestMessageKey = latestMessage?._stableKey || latestMessage?.id || latestMessage?._id;
    if (!latestMessageKey) return;

    const previousMessageKey = lastMessageId.current;
    lastMessageId.current = latestMessageKey;

    if (!previousMessageKey) return;
    if (latestMessageKey === previousMessageKey) return;

    // Work draft -> persisted-id changes must not trigger another scroll.
    // Only a new user turn explicitly resumes following; layout handles replies.
    if (latestMessage?.role === 'user') {
      if (isCoworkerChat && String(latestMessage.content || '').trim() === workQuestionReplyRef.current) {
        workScrollFollower.schedule();
        return;
      }
      // Studio turns are handled below, including snapshots that contain
      // both the new user message and the first assistant response.
      if (isCoworkerChat && isVideoStudioOpenRef.current) return;
      // A queued follow-up lands under a reply that is still streaming. It is
      // not the turn being answered, so it must not move the viewport.
      if (isCoworkerChat && isWorkFollowUpQueueMessage(latestMessage)) return;
      followLiveRef.current = false;
      if (isCoworkerChat) {
        abortScrollRestore();
        requestPinToBottom(true);
      } else {
        // Reveal the sent bubble across the socket round-trip. Once the
        // assistant responds, further growth must leave the viewport alone.
        requestPinToBottom(true, 900, 5000);
      }
    } else if (isCoworkerChat) {
      workScrollFollower.schedule();
    } else if (!followLiveRef.current) {
      // The reply arrived: release the send pin so it cannot chase the
      // tokens (or the async markdown growth of a reply that lands complete).
            clearBottomPin();
    } else if (isAtBottomRef.current) {
      requestPinToBottom(true, 900, 5000, { followLive: true });
    }
  }, [chatData.messages, requestPinToBottom, isCoworkerChat, clearBottomPin, abortScrollRestore, workScrollFollower]);

  useEffect(() => {
    if (!isCoworkerChat) return;
    let latestUser;
    for (let i = chatData.messages.length - 1; i >= 0; i -= 1) {
      if (chatData.messages[i].role === 'user') {
        latestUser = chatData.messages[i];
        break;
      }
    }
    const key = latestUser?._stableKey || latestUser?.id || latestUser?._id;
    if (!key) return;
    const previous = studioUserTurnRef.current;
    studioUserTurnRef.current = key;
    if (!previous || previous === key || !isVideoStudioOpenRef.current) return;
    if (String(latestUser.content || '').trim() === workQuestionReplyRef.current) return;
    // Redo / lock-take actions bypass the composer. Release any saved-history
    // restore and reveal the submitted turn after it actually enters the
    // transcript. Keep following layout and tool updates until the user scrolls.
    abortScrollRestore();
    requestPinToBottom(false, 900, 5000, { followLive: true });
  }, [chatData.messages, isCoworkerChat, abortScrollRestore, requestPinToBottom]);

  // Work threads keep their run timer and each tool step in the transcript
  // rather than in a panel above the composer. Both need the same centered
  // column the bubbles use.
  const workColumnStyle = useMemo(() => ({
    width: '100%',
    maxWidth: MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'] || MESSAGE_WIDTH_AMOUNTS.NARROW,
    alignSelf: 'center',
    paddingHorizontal: 16,
  }), [messageWidth]);
  const workTurns = useMemo(
    () => (isCoworkerChat ? buildWorkTurns(coworker.events) : null),
    [isCoworkerChat, coworker.events],
  );
  const workMessageKey = useMemo(
    () => (isCoworkerChat ? workMessageProjectionKey(chatData.messages) : ''),
    [isCoworkerChat, chatData.messages],
  );
  const workRuns = useMemo(
    () => (isCoworkerChat && !coworker.launchWait
      ? buildWorkRuns(chatData.messages, coworker.events, {
        isActive: coworker.isActive,
        organizerModel: coworker.task?.model,
        creditReceipts: coworker.task?.creditReceipts,
        turns: workTurns,
      })
      : null),
    // workMessageKey, not chatData.messages: streaming only changes content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCoworkerChat, workMessageKey, workTurns, coworker.isActive, coworker.launchWait, coworker.task?.model, coworker.task?.creditReceipts],
  );
  const workStartupPrompt = useMemo(() => {
    if (!isCoworkerChat) return '';
    const messages = chatData.messages || [];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user' && typeof messages[i].content === 'string' && messages[i].content.trim()) return messages[i].content;
    }
    return coworker.task?.prompt || '';
  }, [isCoworkerChat, chatData.messages, coworker.task?.prompt]);
  const workLiveValue = useWorkLiveValue({
    startupPrompt: workStartupPrompt,
    taskError: coworker.task?.error,
    cancellationPending: coworker.task?.cancellationPending === true,
    taskId: coworker.taskId,
    taskStatus: coworker.taskStatus,
    isActive: coworker.isActive,
    activityStatus: coworker.activityStatus,
    activityIcon: coworker.activityIcon,
    activityStartedAt: coworker.activityStartedAt,
    lastEventAt: coworker.lastEventAt,
    browserState: coworker.browserState,
    // While a settled follow-up/resume request is being accepted, the old
    // event log still ends at the previous session. Use the optimistic start
    // so the live clock never counts the paused time as active work.
    liveStartTs: coworker.optimisticLiveStartedAt ?? workRuns?.live?.startTs ?? null,
  });

  // Scrolled up to read history while Work is still going: the scroll-to-
  // bottom button becomes a "jump to live" pill carrying the current
  // activity, so the reader knows the run is alive without leaving the spot.
  const workJumpToLiveLabel = useMemo(() => {
    if (!isCoworkerChat || !coworker.isActive) return null;
    const messages = chatData.messages || [];
    let lastAssistantText = '';
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant') {
        lastAssistantText = messages[i].content || '';
        break;
      }
    }
    if (!shouldShowWorkLiveActivity({
      isActive: true,
      events: coworker.events,
      lastAssistantText,
    })) return null;
    const status = String(coworker.activityStatus || WORK_THINKING_STATUS).trim();
    if (!status) return null;
    return status.length > 44 ? `${status.slice(0, 43).trimEnd()}…` : status;
  }, [isCoworkerChat, coworker.isActive, coworker.activityStatus, coworker.events, chatData.messages]);

  // Token batches grow existing rows. Work follows measured activity with a
  // per-reply reading limit; normal chat follows only after an explicit jump.
  const liveTailLen = useMemo(() => {
    const msgs = chatData.messages || [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m && (m.isStreaming || m.isTyping || m.isThinking)) {
        return String(m.content || '').length + String(m.thinkingContent || '').length;
      }
    }
    return 0;
  }, [chatData.messages]);
  const streamingReply = useMemo(
    () => findStreamingReply(chatData.messages),
    [chatData.messages],
  );
  streamingReplyRef.current = streamingReply;
  const pinLiveRafRef = useRef(0);
  useEffect(() => {
    if (isCoworkerChat) {
      workScrollFollower.schedule();
      return;
    }
    if (anchorPinRef.current) return;
    const followLive = followLiveRef.current;
    const mode = getStreamScrollMode({
      hasStreamingText: !!streamingReply?.id,
      followLive,
      isCoworkerChat,
    });
    if (mode === WORK_STREAM_SCROLL.HOLD) {
      clearBottomPin();
      return undefined;
    }
    if (!followLive) return undefined;
    if (pinLiveRafRef.current) return undefined;
    pinLiveRafRef.current = requestAnimationFrame(() => {
      pinLiveRafRef.current = 0;
      if (!followLiveRef.current || anchorPinRef.current) return;
      pinToBottomNow(false);
    });
    return () => {
      if (pinLiveRafRef.current) {
        cancelAnimationFrame(pinLiveRafRef.current);
        pinLiveRafRef.current = 0;
      }
    };
  }, [liveTailLen, streamingReply?.id, streamingReply?.length, coworker.events?.length, coworker.activityStatus, chatData.messages?.length, isCoworkerChat, pinToBottomNow, clearBottomPin, workScrollFollower]);

  // Empty streaming drafts stay out of the list; every other Work reply stays
  // in the transcript with its tool steps underneath.
  const visibleMessages = useMemo(() => {
    const hidden = workRuns?.hiddenMessageIds;
    const messages = chatData.messages || [];
    if (!hidden?.size) return messages;
    return messages.filter((message) => !hidden.has(message.id));
  }, [chatData.messages, workRuns]);

  if (isCoworkerChat) {
    const messages = visibleMessages;
    let replyIndex = messages.length - 1;
    while (replyIndex >= 0 && messages[replyIndex]?.role !== 'assistant') replyIndex -= 1;
    const reply = messages[replyIndex];
    workScrollFollower.setReply(reply ? chatData.messages.indexOf(reply) : -1, reply?.id || null);
  }

  // Group consecutive assistant messages. Group objects are reused from the
  // previous pass when their member messages are the same references, so a
  // streaming flush (which only replaces the one message being streamed)
  // does not hand every MessageGroup in a long thread a fresh props object
  // and force it to re-render.
  const groupCacheRef = useRef(new Map());
  const groupedResult = useMemo(() => {
    const rawMessages = visibleMessages;
    if (!rawMessages || rawMessages.length === 0) {
      return { items: [], nextGroups: new Map() };
    }

    const grouped = [];
    const previousGroups = groupCacheRef.current;
    const nextGroups = new Map();

    rawMessages.forEach((msg) => {
      const isAssistant = msg.role === 'assistant';
      const hasParent = msg.parentMessageId && msg.parentMessageId !== null;
      // Work replies carry their own trails and live status. Folding them
      // into MessageGroup skipped that chrome and left a finished bubble
      // as the last word while the run was still going.
      const isGroupable = isAssistant && hasParent && !isCoworkerChat;

      const lastItem = grouped[grouped.length - 1];

      if (isGroupable && lastItem && lastItem.type === 'group' && lastItem.parentMessageId === msg.parentMessageId) {
        lastItem.messages.push(msg);
      } else if (isGroupable) {
        grouped.push({
          type: 'group',
          id: `group-${msg.parentMessageId}`,
          parentMessageId: msg.parentMessageId,
          messages: [msg],
          role: 'assistant',
        });
      } else {
        grouped.push(msg);
      }
    });

    for (let i = 0; i < grouped.length; i++) {
      const item = grouped[i];
      if (item.type !== 'group') continue;
      const previous = previousGroups.get(item.id);
      if (
        previous
        && previous.messages.length === item.messages.length
        && previous.messages.every((m, idx) => m === item.messages[idx])
      ) {
        grouped[i] = previous;
        nextGroups.set(item.id, previous);
      } else {
        nextGroups.set(item.id, item);
      }
    }
    // Insert a date separator before the first item of each calendar day.
    // Separators are derived from the item timestamps, so their ids stay
    // stable across streaming flushes and FlatList never treats them as
    // removed-then-added.
    const withSeparators = [];
    let previousDayKey = null;
    for (const item of grouped) {
      const ts = item.type === 'group' ? item.messages?.[0]?.timestamp : item.timestamp;
      if (ts) {
        const d = new Date(ts);
        const dayKey = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        if (dayKey !== previousDayKey) {
          withSeparators.push({
            type: 'date-separator',
            id: `date-separator-${dayKey}`,
            _stableKey: `date-separator-${dayKey}`,
            dayTimestamp: ts,
          });
          previousDayKey = dayKey;
        }
      }
      withSeparators.push(item);
    }

    return { items: withSeparators, nextGroups };
  }, [visibleMessages, isCoworkerChat]);
  const groupedMessages = groupedResult.items;
  useEffect(() => {
    groupCacheRef.current = groupedResult.nextGroups;
  }, [groupedResult]);

  useEffect(() => {
    tracksAnchorInStateRef.current = !isCoworkerChat;
    workEventsReadyRef.current = !isCoworkerChat
      || !coworker.isActive
      || coworkerHasSnapshot
      || (coworker.events?.length > 0);
  }, [isCoworkerChat, coworker.isActive, coworkerHasSnapshot, coworker.events?.length]);

  // ── Conversation timeline rail (desktop web) ─────────────────────────────
  // One entry per user prompt; hovering the right-edge rail expands it into a
  // clickable list that jumps the list to that turn.
  //
  // When a contextual sidebar (document/presentation/spreadsheet editor,
  // PDF preview, etc.) is open, the chat pane can get too narrow for the rail
  // and its 288px hover panel to sit beside the message column without
  // overlapping content — so it hides until there's room again (resizing the
  // divider updates contextSidebarWidth live).
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarWidth = useUIStore(state => getEffectiveContextSidebarWidth(state, windowWidth));
  const availableChatWidth = (windowWidth || 0) - (isContextSidebarVisible ? (contextSidebarWidth || 0) : 0);
  const showTimelineRail = Platform.OS === 'web'
    && !isXS
    && !isCompact
    && !isCoworkerChat
    && availableChatWidth >= 1150;

  // Center the transcript and composer between navigation and Sources.
  // Narrow them together before hiding the Sources card.
  const navigationSidebarWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  // Dismissing navigation should leave the reading column where it was.
  // Keep its gutter on wide Mac conversations, but give compact chats and
  // artifact/detail splits all of their available space.
  const collapsedSidebarInset = Platform.OS === 'macos'
    && !isCompactMacWindow(windowWidth)
    && !isCompact
    && !isContextSidebarVisible
    ? Math.max(0, getMacSidebarWidth(windowWidth) - navigationSidebarWidth)
    : 0;
  const [measuredChatPaneWidth, setMeasuredChatPaneWidth] = useState(0);
  const showSourcesOutputsPref = useUIStore(state => state.showSourcesOutputsPanel !== false);
  const estimatedChatPaneWidth = (windowWidth || 0)
    - (isXS && Platform.OS !== 'macos' ? 0 : navigationSidebarWidth)
    - collapsedSidebarInset
    - (isContextSidebarVisible ? (contextSidebarWidth || 0) : 0);
  const chatPaneWidth = Platform.OS === 'macos'
    ? Math.min(measuredChatPaneWidth || estimatedChatPaneWidth, estimatedChatPaneWidth)
    : estimatedChatPaneWidth;
  const sourcesOutputsLayout = resolveSourcesOutputsLayout({
    chatPaneWidth,
    minChatWidth: Platform.OS === 'macos' ? MAC_MIN_CHAT_WIDTH : undefined,
    artifactOpen: isContextSidebarVisible && isArtifactSidebarType(contextSidebarType),
  });
  const showSourcesOutputsPanel = showSourcesOutputsPref
    && (Platform.OS === 'web' || Platform.OS === 'macos')
    && (Platform.OS !== 'macos' || !isCompactMacWindow(windowWidth))
    && !isXS
    && !isCompact
    && sourcesOutputsLayout.canShowPanel;
  const composerFilesForSources = useChatInputStore(
    (state) => state.inputStates?.[id]?.files ?? EMPTY_COMPOSER_FILES
  );
  // The Sources card and the timeline rail share the right gutter. Feeding the
  // column height into the card bounds its scroll area, and the height it
  // reports back keeps the rail's hover panel from opening underneath it.
  const [messagesAreaHeight, setMessagesAreaHeight] = useState(0);
  const [sourcesPanelHeight, setSourcesPanelHeight] = useState(0);
  const handleMessagesAreaLayout = useCallback((event) => {
    const height = event?.nativeEvent?.layout?.height ?? 0;
    setMessagesAreaHeight(prev => (Math.abs(prev - height) < 1 ? prev : height));
    const width = event?.nativeEvent?.layout?.width;
    if (Number.isFinite(width) && width > 0) {
      setMeasuredChatPaneWidth(prev => Math.abs(prev - width) < 1 ? prev : width);
    }
  }, []);
  const handleSourcesPanelHeightChange = useCallback((height) => {
    const next = Number.isFinite(height) ? height : 0;
    setSourcesPanelHeight(prev => (Math.abs(prev - next) < 1 ? prev : next));
  }, []);
  // Structure-only key: the panel reads files / images / videos / citations /
  // artifacts, never message text, so streaming token flushes (which only
  // change content) must not re-collect the panel ~20 times a second.
  const sourcesStructureKey = useMemo(() => {
    if (!showSourcesOutputsPanel) return '';
    let key = '';
    for (const m of chatData.messages) {
      key += `${m?.id || ''}:${m?.files?.length || 0}:${m?.images?.length || 0}:${m?.videos?.length || 0}:${m?.citations?.length || 0}:${m?.parameters?.coworkerArtifacts?.length || 0}|`;
    }
    return key;
  }, [showSourcesOutputsPanel, chatData.messages]);
  const {
    inputs: conversationInputs,
    outputs: conversationOutputs,
    sources: conversationSources,
  } = useMemo(() => {
    if (!showSourcesOutputsPanel) return { inputs: [], outputs: [], sources: [] };
    const collected = collectSourcesAndOutputs(chatData.messages, composerFilesForSources,
      isCoworkerChat ? { task: coworker.task, taskId: coworker.taskId, conversationId: id } : {});
    // Work replies carry no citations, so visited pages and local files
    // referenced by completed read/diff operations are the thread's sources.
    if (!isCoworkerChat) return collected;
    const visited = workSourcesFromEvents(coworker.events).map((entry) => ({
      kind: 'link',
      id: `work-source-${entry.seq}`,
      url: entry.url,
      title: entry.host,
      hostname: entry.host,
    }));
    const projectFiles = Platform.OS === 'macos' && coworker.task?.localProject?.bindingId
      && !coworker.task.localProject.revokedAt
      ? workProjectSourcesFromEvents(coworker.events).map((entry) => ({
        kind: 'project_file',
        id: `project-source-${entry.path}`,
        path: entry.path,
      })) : [];
    if (!visited.length && !projectFiles.length) return collected;
    const known = new Set(collected.sources.map((entry) => entry.url).filter(Boolean));
    return {
      inputs: collected.inputs,
      outputs: collected.outputs,
      sources: [...collected.sources, ...projectFiles, ...visited.filter((entry) => !known.has(entry.url))],
    };
    // sourcesStructureKey stands in for chatData.messages: token streaming
    // changes content only, and content never feeds this panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSourcesOutputsPanel, sourcesStructureKey, composerFilesForSources, isCoworkerChat, coworker.events,
    coworker.taskId, coworker.task?.id, coworker.task?.conversationId, coworker.task?.artifacts, id,
    coworker.task?.localProject?.bindingId, coworker.task?.localProject?.revokedAt]);
  const hasSourcesOrOutputs = conversationInputs.length > 0
    || conversationOutputs.length > 0
    || conversationSources.length > 0;
  const setVideoProjectContext = useUIStore(state => state.setVideoProjectContext);
  const videoScriptArchiveRef = useRef({ conversationId: null, fetching: false, done: false });
  const videoStudioGateRef = useRef({ conversationId: null, autoOpened: false, dismissed: false });
  const videoProjectKey = useMemo(() => {
    let key = `${id || ''}:${coworker.taskId || ''}|`;
    for (const message of chatData.messages) {
      key += `${message?.id || ''}:${message?.files?.length || 0}:${message?.images?.length || 0}:${message?.videos?.length || 0}:${message?.parameters?.coworkerArtifacts?.length || 0}|`;
      for (const artifact of message?.parameters?.coworkerArtifacts || []) {
        key += `${artifact?.path || artifact?.name || ''}:${artifact?.size || 0}|`;
      }
      if (message?.role !== 'assistant') continue;
      const text = typeof message.content === 'string' ? message.content : '';
      if (text.length > 80 && /(?:shot|clip)\s+\d+/i.test(text)) {
        key += `${text.length}:${text.slice(0, 20)}:${text.slice(-20)}|`;
      }
    }
    return key;
  }, [id, coworker.taskId, chatData.messages]);
  const videoStudioProject = useMemo(() => {
    if (!isCoworkerChat) return null;
    const collected = collectVideoStudioInventory({ messages: chatData.messages,
      task: coworker.task, taskId: coworker.taskId, conversationId: id, summary: chat?.summary });
    const stored = useUIStore.getState().videoProjectContext;
    return buildVideoProject({
      artifacts: collected.artifacts,
      files: collected.files,
      scriptText: collected.scriptText || (stored?.conversationId === id ? stored.scriptText : '') || '',
    });
    // videoProjectKey stands in for chatData.messages so token streaming
    // does not rebuild the studio on every token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoworkerChat, videoProjectKey, coworker.task?.artifacts, coworker.task?.context, coworker.task?.referenceFiles]);
  const mediaStudioKind = useMemo(() => conversationMediaStudio({
    project: videoStudioProject,
    messages: chatData.messages,
  }), [videoStudioProject, videoProjectKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!coworker.taskId || !coworker.followUpQueue?.length) return;
    for (const item of coworker.followUpQueue) {
      if (isObsoleteVideoFollowUp(item.text, { events: coworker.events, project: videoStudioProject })) {
        coworker.removeFollowUp(item.id);
      }
    }
  }, [coworker.events, coworker.followUpQueue, coworker.removeFollowUp, coworker.taskId, videoStudioProject]);
  const [videoReviewHiddenGate, setVideoReviewHiddenGate] = useState(null);
  useEffect(() => {
    setVideoReviewHiddenGate(null);
    useUIStore.getState().clearVideoTakePicks();
  }, [id, coworker.taskId]);
  useEffect(() => {
    const gate = videoProjectReviewGate(videoStudioProject, { events: coworker.events });
    if (videoReviewHiddenGate && gate !== videoReviewHiddenGate) {
      setVideoReviewHiddenGate(null);
    }
  }, [coworker.events, videoStudioProject, videoReviewHiddenGate]);
  const videoReviewQuestion = useMemo(() => {
    if (!isCoworkerChat || !coworker.taskId || coworker.isActive) return null;
    if (coworker.browserState?.question || coworker.browserState?.confirmation) return null;
    const gate = videoProjectReviewGate(videoStudioProject, { events: coworker.events });
    if (!gate || gate === videoReviewHiddenGate) return null;
    return workVideoReviewQuestion(videoStudioProject, { events: coworker.events });
  }, [
    coworker.browserState?.confirmation,
    coworker.browserState?.question,
    coworker.events,
    coworker.isActive,
    coworker.taskId,
    isCoworkerChat,
    videoReviewHiddenGate,
    videoStudioProject,
  ]);
  const composerQuestion = coworker.browserState?.question || videoReviewQuestion;
  useEffect(() => {
    const collected = collectVideoStudioInventory({ messages: chatData.messages,
      task: coworker.task, taskId: coworker.taskId, conversationId: id, summary: chat?.summary });
    const previous = useUIStore.getState().videoProjectContext;
    const keptScript = previous?.conversationId === id ? previous.scriptText : '';
    const ctx = {
      conversationId: id,
      taskId: coworker.taskId || null,
      artifacts: collected.artifacts,
      files: collected.files,
      scriptText: collected.scriptText || keptScript || '',
    };
    setVideoProjectContext(ctx);
    const studioOpen = !!getOpenVideoProjectProps(id);
    if (videoStudioGateRef.current.conversationId !== id) {
      videoStudioGateRef.current = { conversationId: id, autoOpened: studioOpen, dismissed: false };
    }
    const gate = videoStudioGateRef.current;
    if (gate.autoOpened && !studioOpen) gate.dismissed = true;
    if (studioOpen) gate.autoOpened = true;
    if (studioOpen) {
      maybeAutoOpenVideoProject({ ...ctx, onlyIfOpen: true });
    } else if (mediaStudioKind === 'video' && !gate.dismissed && firstVideoProjectMedia(ctx)) {
      if (maybeAutoOpenVideoProject(ctx)) gate.autoOpened = true;
    }
    const archivedCount = chat?.contextWindow?.archivedMessageCount || chat?.stats?.archivedMessages || 0;
    if (videoScriptArchiveRef.current.conversationId !== id) {
      videoScriptArchiveRef.current = { conversationId: id, fetching: false, done: false };
    }
    const archiveGate = videoScriptArchiveRef.current;
    if (
      isCoworkerChat
      && id
      && !ctx.scriptText
      && archivedCount > 0
      && !archiveGate.done
      && !archiveGate.fetching
    ) {
      archiveGate.fetching = true;
      const isCurrent = () => videoScriptArchiveRef.current === archiveGate
        && useUIStore.getState().videoProjectContext?.conversationId === id;
      loadVideoStudioScript({ conversationId: id, fetchPage: chatsApi.getArchivedConversationMessages, isCurrent })
        .then((scriptText) => {
          archiveGate.fetching = false;
          archiveGate.done = true;
          if (!scriptText || !isCurrent()) return;
          const next = {
            ...(useUIStore.getState().videoProjectContext || {}),
            conversationId: id,
            taskId: coworker.taskId || null,
            scriptText,
          };
          setVideoProjectContext(next);
          maybeAutoOpenVideoProject({ ...next, onlyIfOpen: true });
        })
        .catch(() => {
          archiveGate.fetching = false;
          archiveGate.done = true;
        });
    }
    // videoProjectKey stands in for chatData.messages so token streaming
    // does not rebuild the studio on every token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoProjectKey,
    coworker.task?.artifacts,
    coworker.task?.context,
    coworker.task?.referenceFiles,
    id,
    coworker.taskId,
    setVideoProjectContext,
    chat?.summary,
    isCoworkerChat,
    mediaStudioKind,
    chat?.contextWindow?.archivedMessageCount,
    chat?.stats?.archivedMessages,
  ]);
  useEffect(() => () => {
    const current = useUIStore.getState().videoProjectContext;
    if (current?.conversationId === id) setVideoProjectContext(null);
  }, [id, setVideoProjectContext]);
  useEmbeddedBrowser(coworker.taskId, !isMacProjectReadOnly && isCoworkerChat && isAuthenticated && isOwner);
  const openBrowserViewer = useCoworkerStore(state => state.openBrowserViewer);
  const closeBrowserViewer = useCoworkerStore(state => state.closeBrowserViewer);
  const browserViewerTaskId = useCoworkerStore(state => state.browserViewerTaskId);
  const browserViewerWatch = useCoworkerStore(state => state.browserViewerWatch);
  const showBrowserPanel = !isMacProjectReadOnly && (Platform.OS === 'web' || Platform.OS === 'macos') && !isXS && isCoworkerChat && !!coworker.taskId && browserViewerTaskId === coworker.taskId;
  const handleOpenWorkBrowser = useCallback(
    () => openBrowserViewer(coworker.taskId, { watch: true }),
    [openBrowserViewer, coworker.taskId],
  );
  const workCloudBrowser = useMemo(
    () => ((!isMacProjectReadOnly && isCoworkerChat && coworker.isBrowserActive)
      ? { taskId: coworker.taskId, onOpen: handleOpenWorkBrowser, isLive: coworker.isActive }
      : null),
    [isMacProjectReadOnly, isCoworkerChat, coworker.isBrowserActive, coworker.isActive, coworker.taskId, handleOpenWorkBrowser],
  );
  const conversationOutputFiles = useMemo(
    () => conversationOutputs.flatMap((entry) => (entry.file ? [entry.file] : [])),
    [conversationOutputs]
  );
  const imageStudioFile = useMemo(() => conversationOutputFiles.find(isImageMedia)
    || chatData.messages.filter((message) => message?.role === 'assistant')
      .flatMap((message) => message.files || []).find(isImageMedia),
  [conversationOutputFiles, chatData.messages]);
  const handleOpenVideoStudio = useCallback(() => {
    const stored = useUIStore.getState().videoProjectContext;
    const ctx = stored?.conversationId === id ? stored : {};
    const files = (ctx.files && ctx.files.length) ? ctx.files : conversationOutputFiles;
    maybeAutoOpenVideoProject({
      conversationId: id,
      taskId: coworker.taskId || ctx.taskId || null,
      artifacts: ctx.artifacts,
      files,
      scriptText: ctx.scriptText,
      file: firstVideoProjectMedia({ files, artifacts: ctx.artifacts }) || files.find(isImageMedia),
      videoProject: true,
    });
  }, [conversationOutputFiles, coworker.taskId, id]);
  const handleOpenImageStudio = useCallback(() => {
    if (imageStudioFile) openMessageFile(imageStudioFile, { conversationId: id, videoProject: false });
  }, [imageStudioFile, id]);
  const workVideoStudio = useMemo(() => {
    if (mediaStudioKind === 'video') return { kind: 'video', onOpen: handleOpenVideoStudio };
    if (imageStudioFile) return { kind: 'image', onOpen: handleOpenImageStudio };
    return null;
  }, [imageStudioFile, handleOpenVideoStudio, handleOpenImageStudio, mediaStudioKind]);

  // Scheduled tasks bound to this Work thread (same records as /tasks).
  const workChatTasks = useWorkChatTasks({
    workSessionId: coworker.taskId,
    conversationId: id,
    chatTitle,
    enabled: !isMacProjectReadOnly && isCoworkerChat && isAuthenticated && isOwner && showSourcesOutputsPanel,
  });
  const hasVisibleSourcesPanel = showSourcesOutputsPanel;
  const sourcesReservedRight = resolveSourcesOutputsReservedRight({
    showSourcesOutputsPanel,
    showBrowserPanel,
    reservedRight: sourcesOutputsLayout.reservedRight,
  });
  // The rail's hover card must start below the Sources card.
  const timelineTopInset = hasVisibleSourcesPanel && sourcesPanelHeight > 0
    ? SOURCES_OUTPUTS_PANEL_TOP + sourcesPanelHeight
    : 0;
  // The composer is an absolutely-positioned sibling over this column. The
  // rail has to stop above it or the last prompts in a long thread sit
  // behind the input and cannot be clicked.
  const timelineBottomInset = (isAuthenticated && isOwner && !isDetailed)
    ? inputContainerHeight + workDockGap
    : 0;

  // Stale anchors from the previous conversation must not light up a tick in
  // the next one.
  useEffect(() => {
    viewableAnchorIdRef.current = null;
    setViewableAnchorId(null);
  }, [id]);

  const timelinePrompts = useMemo(() => {
    if (!showTimelineRail) return [];
    const prompts = [];
    for (const m of chatData.messages) {
      if (m.role !== 'user' || !m.id) continue;
      if (isWorkFollowUpQueueMessage(m)) continue;
      const raw = typeof m.content === 'string' ? m.content : '';
      // Only the first line of the preview is shown, so bounding the parse
      // keeps this cheap during streaming re-renders.
      let text = raw ? markdownToPlainText(raw.slice(0, 400)).replace(/\s+/g, ' ').trim() : '';
      if (!text) text = t('chatDetails.nonTextMessage');
      prompts.push({ id: m.id, text });
    }
    return prompts;
  }, [showTimelineRail, chatData.messages, t]);

  // Active prompt = the last user turn at or above the top-most visible
  // message (assistant replies map back to the prompt that produced them).
  const activeTimelinePromptId = useMemo(() => {
    if (!viewableAnchorId || timelinePrompts.length === 0) return null;
    let active = null;
    for (const m of chatData.messages) {
      if (m.role === 'user' && m.id && !isWorkFollowUpQueueMessage(m)) active = m.id;
      if (m.id === viewableAnchorId || m._id === viewableAnchorId) break;
    }
    return active;
  }, [viewableAnchorId, chatData.messages, timelinePrompts.length]);

  const handleTimelinePromptPress = useCallback((messageId) => {
    requestScrollToMessage({ conversationId: id, messageId, viewPosition: 0 });
  }, [requestScrollToMessage, id]);

  // Image-tool chips and "try another model" are chat-only. Gate on the live
  // Work flag — not just the handover-delayed chrome — so a thread that just
  // became Work does not keep suggesting /tools/* while the composer fades.
  const hideMediaSuggestions = isCoworkerChat || coworker.isCoworkerChat;

  const getImageRequestQueryForAssistant = useCallback((message) => {
    if (hideMediaSuggestions) return null;
    return resolveImageRequestUserQuery(message, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  const getImageRequestQueryForGroup = useCallback((group) => {
    if (hideMediaSuggestions) return null;
    return resolveImageRequestUserQueryForGroup(group, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  // A Work run picks its own model for each step and owns a task across many
  // turns, so "try this with another model" has nothing to re-run: it would
  // fork a new chat turn into the middle of the agent's work.
  const getMediaGenerationContextForAssistant = useCallback((message) => {
    if (hideMediaSuggestions) return null;
    return resolveMediaGenerationContext(message, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  const getMediaGenerationContextForGroup = useCallback((group) => {
    if (hideMediaSuggestions) return null;
    return resolveMediaGenerationContextForGroup(group, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  // One-click "try with another model": send a NEW user turn with the same
  // prompt so the previous generation stays visible for comparison.
  const handleTryMediaModel = useCallback(async ({ modelId, model, prompt, action, fileIds = [] }) => {
    if (!prompt?.trim() || !modelId) return;

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'media_model_suggestion' });
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'media_model_suggestion' });
      return;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'media_model_suggestion' });
      return;
    }

    if (requireAgentUpgrade()) return;

    // The server checks the live balance; cached credits must not block a Chat send.

    if (isModelLockedForUser(user, model)) {
      openModelAccessPaywall(openModal, {
        model,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return;
    }

    // Snapshot composer selection so history-seeding from this one-shot
    // message can't pin the tried model into the picker afterward.
    const inputStore = useChatInputStore.getState();
    const inputState = inputStore.inputStates?.[id];
    const prevModelIds = Array.isArray(inputState?.selectedModelIds)
      ? [...inputState.selectedModelIds]
      : [];
    const wasAuto = Boolean(inputState?.userExplicitAutoMode) || prevModelIds.length === 0;

    // Send with the clicked model for this turn only. ___oneShotModel tells
    // seedModelsFromConversationHistory to ignore this message when restoring
    // the composer from history (keeps Auto / prior selection intact).
    const formattedMessage = {
      text: prompt.trim(),
      modelIds: [modelId],
      agentIds: [],
      actions: [],
      fileIds: Array.isArray(fileIds) ? fileIds.filter(Boolean) : [],
      parameters: {
        ...(action ? { action } : {}),
        ___oneShotModel: true,
      },
    };

    const emitOk = socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    if (emitOk === false) return;

    // Lock Auto (or restore the prior selection) so a later history-seed from
    // this message can't rewrite the composer.
    if (inputStore.currentContextId === id) {
      if (wasAuto) {
        inputStore.markContextExplicitAuto?.(id);
        if ((inputStore.inputStates?.[id]?.selectedModelIds || []).length > 0) {
          inputStore.setSelectedModels([]);
        }
      } else {
        inputStore.setSelectedModels(prevModelIds);
      }
    }

    abortScrollRestore();
    followLiveRef.current = false;
    requestPinToBottomRef.current?.(true, 900, 5000);
  }, [
    id,
    userId,
    isPlus,
    isGhost,
    isHeldForReview,
    needsEmailVerification,
    needsPhoneVerification,
    requireAgentUpgrade,
    openModal,
    user,
    abortScrollRestore,
  ]);

  const handleActivateWorkFromComposer = useCallback(() => {
    requireAuth(async () => {
      const text = String(workComposerText || '').trim();
      const fileIds = getFileIds(workComposerFiles);
      if (!text && !fileIds.length) return;
      const created = await startWorkFromPrompt({
        prompt: text,
        fileIds,
        collectionId: chat?.inCollectionId,
        agentId: chat?.agentId,
        router,
        source: 'composer_chip',
      });
      if (created) {
        useChatInputStore.getState().clearInput(true);
      }
    }, {
      source: 'composer_work_mode_suggestion',
      title: t('chatInput.coworkerMode.authTitle'),
      subtitle: t('chatInput.coworkerMode.authSubtitle'),
      icon: 'robot',
    });
  }, [requireAuth, workComposerText, workComposerFiles, chat?.inCollectionId, chat?.agentId, router, t]);

  const handleSendPromptToWork = useCallback(async ({ prompt, fileIds }) => {
    const gated = requireAuth(() => true, {
      source: 'response_work_mode_suggestion',
      title: t('chatInput.coworkerMode.authTitle'),
      subtitle: t('chatInput.coworkerMode.authSubtitle'),
      icon: 'robot',
    });
    if (gated !== true) return;
    await startWorkFromPrompt({
      prompt,
      fileIds,
      collectionId: chat?.inCollectionId,
      agentId: chat?.agentId,
      router,
      source: 'response_card',
    });
  }, [requireAuth, chat?.inCollectionId, chat?.agentId, router, t]);

  const handleWorkModeChoice = useCallback(async (choice, messageId) => {
    if (!isOwner || isInferencing) return;
    if (choice === 'chat' && messageId) {
      const chatStore = useChatStore.getState();
      const existing = chatStore.chats
        .find(c => c.id === id)
        ?.messages
        ?.find(m => String(m.id) === String(messageId) || String(m._id) === String(messageId));
      chatStore.updateLocalChatMessage(id, existing?.id || messageId, {
        parameters: {
          ...(existing?.parameters || {}),
          ___workModeChoice: 'chat',
          ___preferWorkMode: false,
        },
      });
    }
    if (!(await socketManager.requireConnection())) return;
    socketManager.emitKickConversation(userId, id, {
      workModeChoice: choice,
      workModeMessageId: String(messageId),
    });
  }, [isOwner, isInferencing, userId, id]);

  const hasConversationSummaryPill = useMemo(() => {
    return typeof chat?.summary === 'string' && chat.summary.trim().length > 0;
  }, [chat?.summary]);

  const findGroupedIndex = useCallback((items, targetMessageId) => {
    return findGroupedMessageIndex(items, targetMessageId);
  }, []);

  // Re-pin an active restore anchor (non-animated, so corrections are
  // invisible). Called from onContentSizeChange while rows above the anchor
  // are still settling their heights. Self-expires via `until`.
  const repinAnchorNow = useCallback(() => {
    const pin = anchorPinRef.current;
    if (!pin) return;
    if (Date.now() > pin.until) {
      pin.archiveRequest?.cancel();
      anchorPinRef.current = null;
      return;
    }
    if (pin.archiveRequest) {
      const el = getScrollElement();
      const row = el && Array.from(el.querySelectorAll('[id^="msg-hl-"]'))
        .find(node => node.id === `msg-hl-${pin.messageId}`);
      if (row) {
        const top = row.getBoundingClientRect().top - el.getBoundingClientRect().top;
        el.scrollTop += top - pin.archiveTop;
        scrollOffsetRef.current = el.scrollTop;
        pin.archiveRequest.cancel();
        anchorPinRef.current = null;
        chatScrollAnchor.sync(el);
        return;
      }
      const index = findGroupedIndex(groupedMessages, pin.messageId);
      const result = index >= 0 ? pin.archiveRequest.attempt({
        index: index + (hasConversationSummaryPill ? 1 : 0),
        animated: false, viewPosition: 0, viewOffset: pin.archiveTop,
      }) : null;
      if (result?.status === 'stopped') {
        pin.archiveRequest.cancel();
        anchorPinRef.current = null;
        return;
      }
      // A row can replace an equally tall virtual spacer without a resize.
      // Finish that remount through the same bounded request, independently
      // of content-size callbacks. The pin owns and cancels its single timer.
      if (!pin.retryTimer) {
        pin.retryTimer = setTimeout(() => {
          pin.retryTimer = null;
          if (anchorPinRef.current === pin) repinAnchorNowRef.current?.();
        }, 100);
      }
      return;
    }
    if (pin.searchQuery) {
      const ui = useUIStore.getState();
      if (ui.highlightedMessageId !== pin.messageId || ui.highlightedSearchQuery !== pin.searchQuery) {
        anchorPinRef.current = null;
        return;
      }
      // A late row measurement must preserve the phrase, not re-center the
      // entire answer and push that phrase offscreen again.
      scrollSearchMatchIntoView(pin.messageId, getScrollElement(), { query: pin.searchQuery });
      return;
    }
    if (!flatListRef.current?.scrollToIndex) return;
    const index = findGroupedIndex(groupedMessages, pin.messageId);
    if (index < 0) return;
    const listIndex = index + (hasConversationSummaryPill ? 1 : 0);
    indexScroller.scrollToIndex({ index: listIndex, animated: false, viewPosition: pin.viewPosition });
  }, [groupedMessages, findGroupedIndex, hasConversationSummaryPill, indexScroller, getScrollElement, chatScrollAnchor]);
  repinAnchorNowRef.current = repinAnchorNow;

  // Handle scroll requests from other UI (contextual sidebar, etc.)
  useEffect(() => {
    if (isCompact) return;
    if (!pendingScrollRequest) return;
    if (pendingScrollRequest.conversationId !== id) return;

    // A search/sidebar/restore target replaces the reading position captured
    // by an earlier history request. Its retries own any newly inserted rows.
    prependCompensationRef.current = null;
    anchorPinRef.current?.archiveRequest?.cancel();

    // An explicit scroll-to-message must win over any active bottom-pin (e.g.
    // from the just-opened conversation's initial load) or turn reveal. Clear
    // up-front so neither can fight the scrollToIndex retries while we wait
    // for the target item to be laid out.
    clearBottomPin();

    workScrollFollower.pause();
    followLiveRef.current = false;

    const targetMessageId = pendingScrollRequest.messageId;
    const searchQuery = Platform.OS === 'web' ? pendingScrollRequest.searchQuery : null;
    if (searchQuery) anchorPinRef.current = null;
    const scrollViewPosition = pendingScrollRequest.viewPosition ?? 0.25;
    const scrollAnimated = pendingScrollRequest.animated !== false;
    const shouldPinAnchor = pendingScrollRequest.pinAnchor === true;
    const targetIsArchived = pendingScrollRequest.isArchivedMessage === true;

    // Cold-start restore (instant jump + anchor pin): veil the list during
    // positioning so the settle corrections are invisible. Explicit animated
    // jumps (sidebar / search) keep their visible scroll.
    if (shouldPinAnchor && !scrollAnimated) {
      beginListSettleVeil(2500);
    }
    const archiveOriginalIndex = Number.isFinite(pendingScrollRequest.archiveOriginalIndex)
      ? pendingScrollRequest.archiveOriginalIndex
      : null;
    const hydrationKey = `${id}:${targetMessageId}:${pendingScrollRequest.requestedAt}`;
    if (scrollRequestRef.current?.key !== hydrationKey) {
      scrollRequestRef.current?.request.cancel();
      scrollRequestRef.current = { key: hydrationKey, request: indexScroller.createRequest() };
    }
    const scrollRequest = scrollRequestRef.current.request;
    if (archiveScrollHydrationRef.current.key !== hydrationKey) {
      archiveScrollHydrationRef.current = {
        key: hydrationKey,
        didTargetedLoad: false,
        loadsRemaining: archiveOriginalIndex != null ? 6 : 60,
      };
    }
    let attemptsRemaining = 20;
    let cancelled = false;
    const scrollFallback = () => searchQuery
      ? scrollMessageNodeToStart(targetMessageId, getScrollElement())
      : scrollMessageNodeIntoView(targetMessageId, { animated: false });

    const scheduleRetry = (delayMs) => {
      // Clear any previously scheduled retry — only the latest may fire, and
      // the cleanup below can only clear the timer still referenced here.
      if (scrollRequestTimerRef.current) clearTimeout(scrollRequestTimerRef.current);
      scrollRequestTimerRef.current = setTimeout(() => {
        scrollRequestTimerRef.current = null;
        tryScroll();
      }, delayMs);
    };

    const loadArchivedTargetPage = async () => {
      const hydrationState = archiveScrollHydrationRef.current;
      if (!targetIsArchived || hydrationState.loadsRemaining <= 0) return false;

      const latestChat = useChatStore.getState().chats.find(c => c.id === id);
      const latestPagination = latestChat?.archivePagination || {};
      const latestMessages = latestChat?.messages || [];
      const targetAlreadyLoaded = latestMessages.some(message => (
        message?.id === targetMessageId || message?._id === targetMessageId
      ));
      if (targetAlreadyLoaded) return true;
      if (latestPagination.isLoading) return true;

      const archivedCount = latestChat?.contextWindow?.archivedMessageCount
        || latestChat?.stats?.archivedMessages
        || 0;
      const loadedArchivedCount = latestPagination.loadedCount
        || latestMessages.filter(message => message.isArchivedMessage).length
        || 0;
      if (latestPagination.hasMore === false || loadedArchivedCount >= archivedCount) {
        return false;
      }

      hydrationState.loadsRemaining -= 1;
      try {
        if (!hydrationState.didTargetedLoad && archiveOriginalIndex != null) {
          hydrationState.didTargetedLoad = true;
          await fetchArchivedMessages(id, {
            beforeIndex: archiveOriginalIndex + 1,
            limit: 25,
            preserveCursor: true,
          });
        } else {
          await fetchArchivedMessages(id, { limit: 100 });
        }
        return true;
      } catch {
        return false;
      }
    };

    const tryScroll = async () => {
      if (cancelled) return;
      // Store writes happen immediately; effect cleanup may come later on
      // native. A drag/send must win even in that intervening frame.
      const currentRequest = useUIStore.getState().pendingScrollRequest;
      if (currentRequest?.conversationId !== id
        || currentRequest?.requestedAt !== pendingScrollRequest.requestedAt
        || currentRequest?.messageId !== targetMessageId) return;
      if (searchQuery) {
        const ui = useUIStore.getState();
        // Closing find or changing its committed result cancels delayed work.
        if (ui.highlightedMessageId !== targetMessageId || ui.highlightedSearchQuery !== searchQuery) {
          clearPendingScrollRequest();
          return;
        }
        linuxScrollRecovery.pause();
        chatScrollAnchor.pause();
        if (scrollSearchMatchIntoView(targetMessageId, getScrollElement(), { query: searchQuery, animated: scrollAnimated })) {
          if (shouldPinAnchor) {
            anchorPinRef.current = { messageId: targetMessageId, searchQuery, until: Date.now() + 3000 };
          }
          clearPendingScrollRequest();
          return;
        }
      }
      const index = findGroupedIndex(groupedMessages, targetMessageId);

      if (index >= 0 && flatListRef.current?.scrollToIndex) {
        try {
          // The request may clear before its smooth scroll finishes.
          linuxScrollRecovery.pause();
          chatScrollAnchor.pause();
          const listIndex = index + (hasConversationSummaryPill ? 1 : 0);
          const result = scrollRequest.attempt({ index: listIndex, animated: scrollAnimated, viewPosition: searchQuery ? 0 : scrollViewPosition });
          if (result.status === 'stopped') {
            anchorPinRef.current = null;
            clearPendingScrollRequest();
            return;
          }
          if (result.status !== 'scrolled') {
            scheduleRetry(150);
            return;
          }
          if (searchQuery) {
            // Mount an offscreen row first, then wait for its highlight effect.
            // Hidden/collapsed matches fall back to the start of the message.
            if (attemptsRemaining-- > 0) {
              scheduleRetry(120);
            } else {
              scrollFallback();
              clearPendingScrollRequest();
            }
            return;
          }
          if (shouldPinAnchor) {
            // Hold the anchor in place while late-rendering rows settle;
            // onContentSizeChange re-pins until the deadline.
            anchorPinRef.current = {
              messageId: targetMessageId,
              viewPosition: scrollViewPosition,
              until: Date.now() + 3000,
            };
          }
          // Virtualized web lists often accept scrollToIndex before the row
          // is in the DOM. Retry until the highlight node can scrollIntoView.
          if (Platform.OS === 'web') {
            const inView = scrollMessageNodeIntoView(targetMessageId, { animated: false });
            if (!inView && attemptsRemaining > 0) {
              attemptsRemaining -= 1;
              scheduleRetry(120);
              return;
            }
          }
          clearPendingScrollRequest();
        } catch (e) {
          if (attemptsRemaining > 0) {
            attemptsRemaining -= 1;
            scheduleRetry(150);
          } else {
            scrollFallback();
            clearPendingScrollRequest();
          }
        }
      } else if (attemptsRemaining > 0) {
        attemptsRemaining -= 1;
        scheduleRetry(150);
      } else {
        const requestedArchivePage = await loadArchivedTargetPage();
        if (cancelled) return;
        if (requestedArchivePage) {
          attemptsRemaining = 12;
          scheduleRetry(150);
        } else {
          scrollFallback();
          clearPendingScrollRequest();
        }
      }
    };

    scheduleRetry(initialLoadComplete ? 0 : 150);

    return () => {
      cancelled = true;
      if (scrollRequestTimerRef.current) clearTimeout(scrollRequestTimerRef.current);
      scrollRequestTimerRef.current = null;
    };
  }, [pendingScrollRequest?.requestedAt, id, groupedMessages, initialLoadComplete, findGroupedIndex, isCompact, clearBottomPin, clearPendingScrollRequest, fetchArchivedMessages, hasConversationSummaryPill, beginListSettleVeil, indexScroller, workScrollFollower, linuxScrollRecovery, chatScrollAnchor, getScrollElement]);

  // Handle scroll-to-top requests
  useEffect(() => {
    if (isCompact) return;
    if (!pendingScrollToTopRequest) return;
    if (pendingScrollToTopRequest.conversationId !== id) return;

    prependCompensationRef.current = null;

    workScrollFollower.pause();
    followLiveRef.current = false;
    // Scroll-to-top must win over any active bottom-pin.
    abortScrollRestore();
    clearBottomPin();
    linuxScrollRecovery.pause();
    chatScrollAnchor.pause();
    if (flatListRef.current?.scrollToOffset) {
      flatListRef.current.scrollToOffset({ offset: 0, animated: true });
    }
    clearPendingScrollToTopRequest();
  }, [pendingScrollToTopRequest?.requestedAt, id, clearPendingScrollToTopRequest, isCompact, clearBottomPin, abortScrollRestore, workScrollFollower, linuxScrollRecovery, chatScrollAnchor]);

  // Memoize render item
  const lastIndex = useMemo(() => {
    if (!groupedMessages.length) return 0;
    return groupedMessages.length - 1 + (hasConversationSummaryPill ? 1 : 0);
  }, [groupedMessages.length, hasConversationSummaryPill]);
  const handleWorkPlanInteraction = useCallback(() => {
    // Stop before release: a live native scroll can cancel the plan's press.
    // Keep the expanded checklist in place when its height changes, too.
    workScrollFollower.pause();
    followLiveRef.current = false;
    userScrollIntentUntilRef.current = 0;
    clearBottomPin();
    abortScrollRestore();
  }, [workScrollFollower, clearBottomPin, abortScrollRestore]);
  const renderItem = useCallback(({ item, index }) => {
    if (item.type === 'work-launch-wait') {
      return <View style={workColumnStyle}><WorkLaunchWaitNotice waiting={item.waiting} /></View>;
    }
    if (item.type === 'work-question-history') {
      return <View style={workColumnStyle}><WorkQuestionHistoryRow taskId={coworker.taskId} question={item.question}
        readOnly={isMacProjectReadOnly || !isOwner} onAnswered={handleWorkQuestionAnswered}
        onViewInteraction={handleWorkPlanInteraction} /></View>;
    }
    if (isMacProjectReadOnly && ['work-approval', 'work-question'].includes(item.type)) {
      return <View style={[workColumnStyle, { padding: 20, gap: 8 }]}>
        <Text size="m" weight="medium">{item.question?.question || item.confirmation?.action || 'Work needs your response'}</Text>
        <Text size="s" color="secondary">Respond in the Mac app or from Remote on your paired phone.</Text>
      </View>;
    }
    // Live Work run: one status row at the end of the transcript saying what
    // the agent is doing right now. Completed steps live in the trails above.
    if (item.type === 'work-live-activity') {
      return (
        <WorkLiveActivityRow
          style={[workColumnStyle, Platform.OS === 'web' && { overflowAnchor: 'none' }]}
          live={workRuns?.live}
        />
      );
    }
    if (item.type === 'work-upgrade-notice') {
      return <WorkUpgradeNotice style={workColumnStyle} />;
    }
    // Gate cards only exist while a live run is waiting on the user, so a
    // mount ease-in is always the right entrance.
    if (item.type === 'work-approval') {
      return (
        <WorkAppear style={workColumnStyle} duration={220} distance={6}>
          <WorkApprovalCard
            taskId={coworker.taskId}
            confirmation={item.confirmation}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </WorkAppear>
      );
    }
    if (item.type === 'work-question') {
      return (
        <WorkAppear style={workColumnStyle} duration={220} distance={6}>
          <WorkQuestionCard
            key={item.question?.id || coworker.taskId}
            taskId={coworker.taskId}
            question={item.question}
            onAnswered={(text, extra) => {
              if (String(item.question?.id || '').startsWith('video-review-')) {
                setVideoReviewHiddenGate(videoProjectReviewGate(videoStudioProject, { events: coworker.events }));
              }
              return handleWorkQuestionAnswered(text, extra);
            }}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </WorkAppear>
      );
    }
    if (item.type === 'work-plan') {
      return (
        // This persistent document can move as history and the final receipt
        // settle. Keep it in normal layout: a native animated transform can
        // leave the Mac card painted over the preceding receipt after reflow.
        <View style={[workColumnStyle, { paddingVertical: 12 }, Platform.OS === 'web' && { overflowAnchor: 'none' }]}>
          <WorkPlanCard
            readOnly={isMacProjectReadOnly}
            taskId={coworker.taskId}
            plan={item.plan}
            onViewInteraction={handleWorkPlanInteraction}
            onAnswered={handleWorkQuestionAnswered}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </View>
      );
    }
    if (item.isInferencingWaiting || item.id === 'inferencing-waiting') {
      return (
        <MessageWaiting 
          conversationId={item.conversationId}
          inferenceStatus={item.inferenceStatus}
          inferenceStage={item.inferenceStage}
          inferenceElapsedMs={item.inferenceElapsedMs}
          canFastAnswer={item.canFastAnswer}
        />
      );
    }
    if (item.id === 'ghost-message') {
      return <MessageGhostUpsell />
    }
    if (item.type === 'card-expiration-warning') {
      return (
        <CardExpirationWarningMessage
          warningData={cardExpirationWarning}
          onDismiss={handleDismissCardExpirationWarning}
        />
      );
    }
    if (item.type === 'conversation-summary-pill') {
      return (
        <ConversationSummaryPill
          summary={item.summary}
          summarizedMessageCount={item.summarizedMessageCount}
          messageWidth={messageWidth}
        />
      );
    }
    if (item.type === 'date-separator') {
      return <MessageDateSeparator timestamp={item.dayTimestamp} messageWidth={messageWidth} />;
    }

    if (item.type === 'group') {
      return (
        <MessageHighlightWrapper messageIds={item.messages.flatMap(m => [m.id, m._id]).filter(Boolean)}>
          <View>
            <MessageGroup
              messages={item.messages}
              parentMessageId={item.parentMessageId}
              conversationId={id}
              isOwner={isOwner}
              chatTitle={chatTitle}
              isLastGroup={index === lastIndex}
            />
            {index !== lastIndex && !hideMediaSuggestions && (() => {
              const mediaContext = getMediaGenerationContextForGroup(item);
              const imageRequestQuery = getImageRequestQueryForGroup(item);
              if (!mediaContext && !imageRequestQuery) return null;
              return (
                <MediaGenerationSuggestions
                  mediaContext={mediaContext}
                  userQuery={imageRequestQuery}
                  onTryModel={handleTryMediaModel}
                  canTryModels={isOwner && !isInferencing}
                />
              );
            })()}
          </View>
        </MessageHighlightWrapper>
      );
    }

    if (!item.id && item.type !== 'group') {
      console.warn('Message missing ID:', item);
      return null;
    }

    const workRun = isCoworkerChat ? workRuns?.byMessageId?.[item.id] : null;
    const displayItem = isCoworkerChat && item.role === 'user'
      ? workUserMessageForDisplay(item)
      : item;
    if (isCoworkerChat && item.role === 'assistant') {
      return (
        <WorkTranscriptMessage
          item={item}
          index={index}
          lastIndex={lastIndex}
          conversationId={id}
          isOwner={isOwner}
          chatTitle={chatTitle}
          workRun={workRun}
          workColumnStyle={workColumnStyle}
          taskStatus={coworker.taskStatus}
          HighlightWrapper={MessageHighlightWrapper}
          onBodyLayout={handleWorkBodyLayout}
        />
      );
    }

    // A user request in a Work thread carries the run's header and the steps
    // taken right after it — the timeline stays chronological around steers.
    if (isCoworkerChat && item.role === 'user' && workRun) {
      return (
        <MessageHighlightWrapper messageIds={[item.id]}>
          <WorkUserTranscriptRow
            workRun={workRun}
            workColumnStyle={workColumnStyle}
            taskStatus={coworker.taskStatus}
          >
            <Message
              conversationId={id}
              isOwner={isOwner}
              chatTitle={chatTitle}
              message={displayItem}
              isLastMessage={index === lastIndex}
            />
          </WorkUserTranscriptRow>
        </MessageHighlightWrapper>
      );
    }

    return (
      <MessageHighlightWrapper messageIds={[item.id, item._id].filter(Boolean)}>
        <View>
          <Message
            conversationId={id}
            isOwner={isOwner}
            chatTitle={chatTitle}
            message={displayItem}
            isLastMessage={index === lastIndex}
          />
          {index !== lastIndex && item.role === 'assistant' && !hideMediaSuggestions && (() => {
            const mediaContext = getMediaGenerationContextForAssistant(item);
            const imageRequestQuery = getImageRequestQueryForAssistant(item);
            if (!mediaContext && !imageRequestQuery) return null;
            return (
              <MediaGenerationSuggestions
                mediaContext={mediaContext}
                userQuery={imageRequestQuery}
                onTryModel={handleTryMediaModel}
                canTryModels={isOwner && !isInferencing}
              />
            );
          })()}
        </View>
      </MessageHighlightWrapper>
    );
  }, [isMacProjectReadOnly, id, isOwner, chatTitle, lastIndex, cardExpirationWarning, handleDismissCardExpirationWarning, messageWidth, getImageRequestQueryForAssistant, getImageRequestQueryForGroup, getMediaGenerationContextForAssistant, getMediaGenerationContextForGroup, handleTryMediaModel, hideMediaSuggestions, isInferencing, isCoworkerChat, workRuns, workColumnStyle, coworker.taskId, coworker.taskStatus, handleWorkQuestionAnswered, handleWorkPlanInteraction, videoStudioProject, handleWorkBodyLayout]);

  // Set on the first genuine user drag (never by programmatic scrolls), so
  // the top-threshold auto-load below can't be triggered by the list's own
  // positioning scrolls (initial bottom-pin, anchor re-pins).
  const hasUserDraggedRef = useRef(false);
  // Latest { can, run } for the archived-messages auto-load, refreshed every
  // render so handleScroll (stable identity) always reads current state.
  const autoLoadEarlierRef = useRef({ can: false, run: null });

  const handleScroll = useCallback((event) => {
    // Ignoring an unexpected zero-offset event alone leaves the DOM at the
    // top. Recover the measured reading position before updating scroll UI.
    if (recoverLinuxChatScroll()) return;
    const { contentOffset, contentSize, layoutMeasurement } = event?.nativeEvent || {};
    const el = getScrollElement();
    // Rows can mount above the reader between frames of a wheel or touch
    // scroll. A correction dispatches its own scroll event with the settled
    // offset, so this stale one is dropped.
    if (syncChatScrollAnchor(el)) return;
    const offsetY = contentOffset?.y ?? el?.scrollTop;
    const contentH = contentSize?.height ?? el?.scrollHeight;
    const layoutH = layoutMeasurement?.height ?? el?.clientHeight;
    if (offsetY == null || !contentH || !layoutH) return;

    // Keep offset/layout refs fresh on every event (even throttled) so the pin
    // release heuristic below has up-to-date data.
    const prevOffset = scrollOffsetRef.current;
    if (restoreStudioScrollIfSnapped(offsetY, el)) return;
    const looksLikeRemountSnap = offsetY < 32 && prevOffset > 80;
    const userIntent = isUserScrollingRef.current || Date.now() <= userScrollIntentUntilRef.current;
    // Web reports the actual DOM position, including search/header jumps to
    // zero after their pending request clears. Ignoring that event leaves the
    // jump-to-bottom button hidden and the old tail position in memory. The
    // Linux and Studio recovery paths above already handle unexpected resets.
    if (Platform.OS !== 'web' && looksLikeRemountSnap && !userIntent) {
      listLayoutHeightRef.current = layoutH;
      return;
    }
    trackChatPrependScroll(prependCompensationRef.current, {
      offset: el?.scrollTop ?? offsetY, contentHeight: el?.scrollHeight ?? contentH, element: el,
    });
    if (offsetY > prevOffset + 2) lastDownwardScrollAtRef.current = Date.now();
    scrollOffsetRef.current = offsetY;
    listLayoutHeightRef.current = layoutH;

    const canScroll = contentH > layoutH + 20;
    const bottomThreshold = Math.max(100, (listPaddingBottom || 0) + 20);
    const isAtBottom = !canScroll || (offsetY + layoutH >= contentH - bottomThreshold);
    isAtBottomRef.current = isAtBottom;

    // Any upward scroll by the user disarms follow-live and cancels any active
    // bottom pin so user navigation always wins over agent activity.
    //
    // The offset also drops without any gesture when content *shrinks* while
    // we sit at the bottom (a question card resolving, a pending send row
    // being replaced by the shorter server row, a thinking block collapsing):
    // the browser clamps scrollTop to the new maximum. Treating that clamp as
    // "user scrolled up" switched follow-live off right as the agent started
    // streaming, so the next growth left the viewport parked above the tail.
    // A real upward scroll never lands exactly on the max offset (and the
    // wheel/drag handlers already disarm follow-live for genuine gestures).
    const maxOffset = Math.max(0, contentH - layoutH);
    const clampedToMax = offsetY >= maxOffset - 2;
    if (offsetY < prevOffset - 2 && !clampedToMax && userIntent) {
      followLiveRef.current = false;
      workScrollFollower.pause();
      if (wantsBottomRef.current) clearBottomPin();
      abortScrollRestore();
    }

    // A deliberate downward scroll to the live edge resumes bounded Work
    // following. Use a tight threshold so reading near the tail stays manual.
    if (isCoworkerChat && userIntent && offsetY > prevOffset + 2 && offsetY >= maxOffset - 24) {
      workScrollFollower.resume();
    }

    // Auto-load earlier messages when the user scrolls into the top threshold.
    // Driven by real scroll events (gated on an actual drag) rather than
    // FlatList's onStartReached: that prop re-fires on every content-size
    // change while parked near the top, and with uncompensated prepends it
    // cascaded — each page shifted the viewport up, re-arming the next load
    // until the user landed at the very top of the thread. Here, the prepend
    // compensation moves the offset back out of the threshold.
    if (
      hasUserDraggedRef.current
      && userIntent
      && offsetY <= Math.min(layoutH * 0.5, 350)
      && autoLoadEarlierRef.current.can
    ) {
      autoLoadEarlierRef.current.run?.();
    }

    const now = Date.now();
    if (now - lastScrollTime.current < 50) {
      return;
    }
    lastScrollTime.current = now;

    // Normal chat still requires the down-arrow to follow tokens. Work's
    // bounded follower resumes only at the live edge, handled above.
    scheduleShowScrollButton(canScroll && !isAtBottom);
    const nearTop = offsetY < 10;
    setIsNearTop(nearTop);
  }, [clearBottomPin, abortScrollRestore, getScrollElement, listPaddingBottom, restoreStudioScrollIfSnapped, scheduleShowScrollButton, isCoworkerChat, workScrollFollower, recoverLinuxChatScroll, syncChatScrollAnchor]);

  const scrollToBottom = useCallback(() => {
    // Tapping the down-arrow is an explicit "take me to the bottom" — abort any
    // in-flight cold-start restore first, otherwise on long chats its retry
    // loop snaps the viewport back up to the saved anchor right after we land
    // at the bottom. Then jump and keep re-pinning while async content
    // (markdown, code, images) finishes laying out so we reach the true bottom.
    abortScrollRestore();
    requestPinToBottom(false, 700, 3500, { followLive: true });
  }, [requestPinToBottom, abortScrollRestore]);

  const archivedMessageCount = chat?.contextWindow?.archivedMessageCount || chat?.stats?.archivedMessages || 0;
  const autoContinuedWorkChoiceRef = useRef(null);
  useEffect(() => {
    if (isCoworkerChat || !isOwner || workModeDisabled || isInferencing) return;
    const choiceContext = {
      lastItem: groupedMessages[groupedMessages.length - 1],
      messages: chatData.messages,
      isInferencing,
      archivedMessageCount,
    };
    const pending = getPendingWorkModeMessage(choiceContext);
    if (!pending) return;
    // Resume routine requests paused by an older server, as well as opted-out
    // suggestions. Hiding the card alone would leave the answer stuck forever.
    if (workModeSuggestionsEnabled && resolveWorkModeSuggestion(choiceContext)) return;
    const key = String(pending.id || pending._id || '');
    if (!key || autoContinuedWorkChoiceRef.current === key) return;
    autoContinuedWorkChoiceRef.current = key;
    handleWorkModeChoice('chat', key);
  }, [
    workModeSuggestionsEnabled,
    isCoworkerChat,
    isOwner,
    workModeDisabled,
    isInferencing,
    groupedMessages,
    chatData.messages,
    archivedMessageCount,
    handleWorkModeChoice,
  ]);
  const loadedArchivedMessageCount = chat?.archivePagination?.loadedCount
    || (chat?.messages || []).filter(message => message.isArchivedMessage).length
    || 0;
  const canLoadArchivedMessages = !isCompact
    && isChatInStore
    && archivedMessageCount > 0
    && loadedArchivedMessageCount < archivedMessageCount
    && chat?.archivePagination?.hasMore !== false;
  const isLoadingArchivedMessages = chat?.archivePagination?.isLoading === true;

  const findTopVisibleMessageId = useCallback(() => {
    const container = getScrollElement();
    if (container) {
      try {
        const containerTop = container.getBoundingClientRect().top;
        const msgEls = container.querySelectorAll('[id^="msg-hl-"]');
        for (let i = 0; i < msgEls.length; i++) {
          const rect = msgEls[i].getBoundingClientRect();
          if (rect.bottom > containerTop + 10) {
            const msgId = msgEls[i].id.replace('msg-hl-', '');
            if (msgId) return msgId;
          }
        }
      } catch (_) {}
    }
    return viewableAnchorIdRef.current || null;
  }, [getScrollElement]);

  const handleLoadEarlierMessages = useCallback(async () => {
    if (!canLoadArchivedMessages || isLoadingArchivedMessages) return;

    workScrollFollower.pause();
    // Disarm any bottom pins or follow-live immediately so the prepend doesn't yank
    followLiveRef.current = false;
    clearBottomPin();
    abortScrollRestore();

    const anchorMessageId = findTopVisibleMessageId();
    const el = getScrollElement();
    const prevOffset = el ? el.scrollTop : scrollOffsetRef.current;
    const prevHeight = el ? el.scrollHeight : contentHeightRef.current;

    const prepend = {
      prevHeight,
      prevOffset,
      anchorMessageId,
      requestPending: true,
      // Network latency must not expire the reader's position. Once the
      // request settles, allow a bounded window for the inserted rows to lay out.
      until: Infinity,
    };
    prependCompensationRef.current = prepend;
    trackChatPrependScroll(prepend, {
      offset: prevOffset, contentHeight: prevHeight, element: el,
    });

    let archivePin = null;
    if (anchorMessageId) {
      archivePin = {
        messageId: anchorMessageId,
        viewPosition: 0,
        until: Date.now() + 6000,
      };
      anchorPinRef.current = archivePin;
    }

    try {
      const loadedMessages = await fetchArchivedMessages(id);
      // A chat switch or a newer request owns its own position. A stale
      // completion must not change that request's refs.
      if (prependCompensationRef.current !== prepend) return;
      prepend.requestPending = false;
      prepend.until = Date.now() + 6000;
      if (!loadedMessages?.length) {
        prependCompensationRef.current = null;
        if (anchorPinRef.current === archivePin) anchorPinRef.current = null;
      }
    } catch (loadError) {
      if (prependCompensationRef.current === prepend) {
        prependCompensationRef.current = null;
        if (anchorPinRef.current === archivePin) anchorPinRef.current = null;
        addToast(loadError?.message || 'Could not load earlier messages.', 'error');
      }
    }
  }, [addToast, canLoadArchivedMessages, fetchArchivedMessages, id, isLoadingArchivedMessages, findTopVisibleMessageId, clearBottomPin, abortScrollRestore, getScrollElement, workScrollFollower]);

  // Latest gate + runner for the handleScroll top-threshold check above.
  autoLoadEarlierRef.current = {
    can: canLoadArchivedMessages && !isLoadingArchivedMessages,
    run: handleLoadEarlierMessages,
  };

  // PLUS_WINBACK_PRESSURE §2: when the conversation detail endpoint
  // truncated the scrollback for a churned-Plus user, render the
  // gradient locked banner above the visible tail. The banner takes
  // precedence over the "Load earlier messages" archive button —
  // churned-Plus users cannot reach archived messages anyway (the
  // archive endpoint is gated separately), and showing both surfaces
  // would be confusing. When no lock applies, fall back to the normal
  // archive loader.
  const plusMessageLock = chat?.plusMessageLock;
  const showPlusLockBanner = !!plusMessageLock?.isChurnedPlus && (plusMessageLock?.hiddenCount || 0) > 0;
  // Mirror MessageGroup's column math so the locked banner + ceiling line
  // up exactly with the real message bubbles (centered, same max width)
  // instead of spilling to the full viewport width.
  const messageWidthPixels = useMemo(() => {
    const value = MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'];
    if (typeof value === 'string') return value;
    return value || 680;
  }, [messageWidth]);

  const listHeaderComponent = useMemo(() => {
    if (showPlusLockBanner) {
      // Scroll order (top → bottom): slim top bar → blurred preview →
      // boundary banner → readable messages. Scrolling up from the latest
      // messages hits the boundary banner + CTA first; continuing up through
      // the blur sees a slim re-iteration bar at the very top.
      const hasLockedPreview = (plusMessageLock.lockedPreview?.length || 0) > 0;
      return (
        <>
          {hasLockedPreview ? (
            <View style={styles.lockColumn}>
              <View style={[styles.lockColumnInner, { maxWidth: messageWidthPixels }]}>
                <PlusMessageLockBanner
                  variant="top"
                  hiddenCount={plusMessageLock.hiddenCount}
                  visibleCap={plusMessageLock.visibleCap}
                />
              </View>
            </View>
          ) : null}
          <PlusLockedMessageCeiling
            preview={plusMessageLock.lockedPreview}
            conversationId={id}
            chatTitle={chatTitle}
            isOwner={isOwner}
          />
          <View style={styles.lockColumn}>
            <View style={[styles.lockColumnInner, { maxWidth: messageWidthPixels }]}>
              <PlusMessageLockBanner
                variant="boundary"
                hiddenCount={plusMessageLock.hiddenCount}
                visibleCap={plusMessageLock.visibleCap}
              />
            </View>
          </View>
        </>
      );
    }
    if (!canLoadArchivedMessages) return null;

    return (
      <View style={styles.archiveLoaderContainer}>
        <Button
          text={isLoadingArchivedMessages ? 'Loading...' : 'Load earlier messages'}
          prependIcon="chevron-up"
          variant="secondary"
          size="small"
          isLoading={isLoadingArchivedMessages}
          isDisabled={isLoadingArchivedMessages}
          style={styles.archiveLoaderButton}
          onPress={handleLoadEarlierMessages}
        />
      </View>
    );
  }, [showPlusLockBanner, plusMessageLock?.hiddenCount, plusMessageLock?.visibleCap, plusMessageLock?.lockedPreview, messageWidthPixels, id, chatTitle, isOwner, canLoadArchivedMessages, handleLoadEarlierMessages, isLoadingArchivedMessages, styles.lockColumn, styles.lockColumnInner, styles.archiveLoaderButton, styles.archiveLoaderContainer]);

  const listFooterComponent = useMemo(() => {
    let lastItemImageRequestQuery = null;
    let lastItemMediaContext = null;
    let lastItem = null;
    if (groupedMessages.length > 0) {
      lastItem = groupedMessages[groupedMessages.length - 1];
      if (lastItem?.type === 'group') {
        lastItemImageRequestQuery = getImageRequestQueryForGroup(lastItem);
        lastItemMediaContext = getMediaGenerationContextForGroup(lastItem);
      } else if (lastItem?.role === 'assistant' && lastItem?.id) {
        lastItemImageRequestQuery = getImageRequestQueryForAssistant(lastItem);
        lastItemMediaContext = getMediaGenerationContextForAssistant(lastItem);
      }
    }

    const resolvedWorkSuggestion = !isCoworkerChat && isOwner && !workModeDisabled
      ? resolveWorkModeSuggestion({
        lastItem,
        messages: chatData.messages,
        isInferencing,
        archivedMessageCount,
      })
      : null;
    const workSuggestion = resolvedWorkSuggestion && workModeSuggestionsEnabled
      ? resolvedWorkSuggestion
      : null;

    const forkedFrom = coworker?.task?.handoffSource ? null : chat?.forkedFrom;
    if (!lastItemImageRequestQuery && !lastItemMediaContext && !forkedFrom?.conversationId && !workSuggestion) {
      return null;
    }

    return (
      <>
        {workSuggestion ? (
          <WorkModeSuggestions
            key={workSuggestion.userMessageId || 'work-mode'}
            prompt={workSuggestion.prompt}
            fileIds={workSuggestion.fileIds}
            onSendToWork={workSuggestion.requiresChoice
              ? () => handleWorkModeChoice('work', workSuggestion.userMessageId)
              : handleSendPromptToWork}
            onContinueChat={workSuggestion.requiresChoice
              ? () => handleWorkModeChoice('chat', workSuggestion.userMessageId)
              : undefined}
            disabled={!isOwner || isInferencing}
          />
        ) : null}
        {(!hideMediaSuggestions && (lastItemMediaContext || lastItemImageRequestQuery)) ? (
          <MediaGenerationSuggestions
            mediaContext={lastItemMediaContext}
            userQuery={lastItemImageRequestQuery}
            onTryModel={handleTryMediaModel}
            canTryModels={isOwner && !isInferencing}
          />
        ) : null}
        {forkedFrom?.conversationId ? (
          <ConversationForkOrigin
            conversationId={String(forkedFrom.conversationId)}
            messageId={forkedFrom.messageId}
            title={forkedFrom.title}
            isCompact={isCompact}
          />
        ) : null}
      </>
    );
  }, [groupedMessages, id, getImageRequestQueryForAssistant, getImageRequestQueryForGroup, getMediaGenerationContextForAssistant, getMediaGenerationContextForGroup, handleTryMediaModel, hideMediaSuggestions, isOwner, isInferencing, chat?.forkedFrom, coworker?.task?.handoffSource, isCompact, chatData.messages, archivedMessageCount, isCoworkerChat, workModeDisabled, workModeSuggestionsEnabled, handleSendPromptToWork, handleWorkModeChoice]);

  // Memoize list props
  const flatListProps = useMemo(() => {
    const data = [...groupedMessages];

    // If the backend has compacted earlier turns into a rolling summary, surface
    // a small pill at the very top of the visible message list. This is the
    // "no silent loss" signal — the user always knows when older context has
    // been folded into a summary, and can expand it to read what was kept.
    if (hasConversationSummaryPill) {
      data.unshift({
        id: 'conversation-summary-pill',
        type: 'conversation-summary-pill',
        _stableKey: 'conversation-summary-pill',
        summary: chat.summary,
        summarizedMessageCount: chat.summarizedMessageCount || 0,
      });
    }

    const hasInferencingWaiting = data.some((item) => item.id === 'inferencing-waiting' || item.isInferencingWaiting);
    const hasGhostMessage = data.some(item => item.id === 'ghost-message');

    if (chat?.isInferencingWaiting && !hasInferencingWaiting) {
      data.push({
        id: 'inferencing-waiting',
        role: 'assistant',
        content: '',
        isInferencingWaiting: true,
        isWaiting: true,
        inferenceStatus: chat?.pendingInferenceStatus,
        inferenceStage: chat?.pendingInferenceStage,
        inferenceElapsedMs: chat?.pendingInferenceElapsedMs,
        canFastAnswer: chat?.pendingCanFastAnswer,
        conversationId: id,
      });
    }
    // Work thread: progress and anything the run needs from the user land at
    // the end of the transcript, in the order the user should read them —
    // what it is doing now, then what it is waiting on.
    if (isCoworkerChat) {
      const { question } = coworker.browserState || {};
      // Why the thread changed shape, stated once, directly under the message
      // that caused it — an upgrade only ever happens on the opening turn, so
      // the note stays anchored there as the run appends replies below. Set
      // only for an auto-upgrade; threads opened in Work mode explain nothing.
      if (chat?.wasUpgradedToWork) {
        const firstUserIndex = data.findIndex((item) => item.role === 'user');
        const notice = {
          id: 'work-upgrade-notice',
          type: 'work-upgrade-notice',
          _stableKey: 'work-upgrade-notice',
        };
        if (firstUserIndex >= 0) data.splice(firstUserIndex + 1, 0, notice);
        else data.push(notice);
      }
      // One status row at the end of the transcript for the whole run.
      // Growing text and a finished comment used to hide this — tokens
      // pause between tools, so the thread looked idle while work continued.
      // Plan card stays attached to the turn that published it (View plan /
      // Build), like a file in the thread. The composer gate owns it while
      // approval is open so it never appears twice.
      if (shouldShowWorkPinnedPlan({
        plan: coworker.plan,
        questionKind: question?.kind,
      })) {
        const planRow = { id: 'work-plan', type: 'work-plan', _stableKey: 'work-plan', plan: coworker.plan };
        data.splice(workPlanAttachIndex(data, coworker.events, {
          isQueuedMessage: isWorkFollowUpQueueMessage,
          plan: coworker.plan,
        }), 0, planRow);
      }
      if (coworker.launchWait) {
        data.push({ id: 'work-launch-wait', type: 'work-launch-wait', _stableKey: 'work-launch-wait', waiting: coworker.launchWait });
      } else if (shouldShowWorkLiveActivity({
        isActive: coworker.isActive || !!workRuns?.live,
        hasInferencingWaiting,
        events: coworker.events,
        lastAssistantText: [...(chatData.messages || [])].reverse().find((message) => message?.role === 'assistant')?.content,
      })) {
        data.push({ id: 'work-live-activity', type: 'work-live-activity', _stableKey: 'work-live-activity' });
      }
      // Film-review gates pin above the composer. Keep them out of the
      // transcript whenever that pin is on screen so the same card does not
      // stack twice. If the composer is hidden (detailed input, signed-out,
      // or a shared thread), land the box in the list instead.
      const composerPinsQuestion = !isDetailed && isAuthenticated && isOwner;
      const historyRows = insertWorkQuestionHistory(data, coworker.questionHistory, composerPinsQuestion ? composerQuestion?.id : null);
      data.splice(0, data.length, ...historyRows);
      if (videoReviewQuestion && !composerPinsQuestion) {
        data.push({
          id: videoReviewQuestion.id,
          type: 'work-question',
          _stableKey: videoReviewQuestion.id,
          question: videoReviewQuestion,
        });
      }
      // A sign-in ask takes over the composer instead of appearing here: it is
      // a form the user types into, so it belongs where typing happens.
      // Live ask_user cards sit in the composer.
    }
    if (isGhost && !hasGhostMessage) {
      data.push({ id: 'ghost-message', role: 'assistant', content: 'Please connect your email to continue your conversation.', isGhost: true });
    }
    
    const hasCardExpirationWarning = data.some(item => item.id === 'card-expiration-warning');
    if (showCardExpirationWarning && !hasCardExpirationWarning && !chat?.isInferencingWaiting) {
      data.push({ id: 'card-expiration-warning', type: 'card-expiration-warning' });
    }

    const baseProps = {
      ref: flatListRef,
      data: data,
      renderItem: renderItem,
      contentContainerStyle: listContentContainerStyle,
      ...(Platform.OS === 'web' ? { style: { overflowAnchor: 'none' } } : {}),
      showsVerticalScrollIndicator: showsScrollIndicator && !isSettleVeilUp,
      inverted: false,
      // Native layout can replace estimated row heights as code/markdown is
      // measured. Preserve the visible row through that reflow. The Mac
      // mount observer yields to explicit scroll commands in the same batch.
      ...(Platform.OS === 'macos' ? { maintainVisibleContentPosition: { minIndexForVisible: 0 } } : {}),
      // 'interactive' is iOS-only; Android needs 'on-drag' for list-drag
      // keyboard dismissal (web ignores the prop either way). iPad's
      // interactive mode dismisses the IME as soon as keyboard insets
      // change the list, so leave it off there.
      keyboardDismissMode: Platform.isPad ? 'none' : (Platform.OS === 'ios' ? 'interactive' : 'on-drag'),
      keyboardShouldPersistTaps: 'handled',
      // Work rows carry deterministic ids from the projection, so the id is
      // the key. Chat rows keep `_stableKey` for locally-created bubbles that
      // are later renamed by the server.
      keyExtractor: (item) => (
        isCoworkerChat
          ? (item.id || item._stableKey || item.key || String(item))
          : (item._stableKey || item.id || item.key || String(item))
      ),
      onScroll: handleScroll,
      onContentSizeChange: (_contentWidth, contentHeight) => {
        recoverLinuxChatScroll();
        if (!initialLoadComplete) {
          setInitialLoadComplete(true);
        }
        // While the settle veil is up, every content reflow restarts the
        // quiet timer; the list is revealed once changes stop.
        bumpListSettleVeil();
        const prevContentHeight = contentHeightRef.current;
        contentHeightRef.current = contentHeight;
        const el = getScrollElement();
        const currentScrollY = el ? el.scrollTop : scrollOffsetRef.current;
        const currentLayoutH = el ? el.clientHeight : listLayoutHeightRef.current;
        const bottomThreshold = Math.max(100, (listPaddingBottom || 0) + 20);
        const wasAtBottom = isAtBottomRef.current || (currentLayoutH > 0 && (
          currentScrollY + currentLayoutH >= prevContentHeight - (bottomThreshold + 40)
          || prevContentHeight <= currentLayoutH
        ));
        if (currentLayoutH > 0) {
          const isAtBottom = contentHeight <= currentLayoutH || currentScrollY + currentLayoutH >= contentHeight - bottomThreshold;
          isAtBottomRef.current = isAtBottom;
        }

        // Prepend compensation: keep the viewport on the current message when
        // earlier messages are loaded above it.
        const prepend = prependCompensationRef.current;
        if (prepend?.requestPending) {
          // Late markdown/layout changes while the network request is still
          // pending are not archived rows. Keep them out of the prepend delta.
          prepend.prevHeight = contentHeight;
          trackChatPrependScroll(prepend, {
            offset: currentScrollY, contentHeight, element: el,
          });
        } else if (prepend && Date.now() <= prepend.until
          && (prepend.requestPending === false || contentHeight > prepend.prevHeight)) {
          const rowAnchor = prepend.rowAnchor;
          if (el && rowAnchor && !rowAnchor.node.isConnected && flatListRef.current?.scrollToIndex) {
            // A full archive page can move the current row outside the render
            // window. Mount that same row with bounded index recovery, then
            // restore its measured viewport position when it renders again.
            prependCompensationRef.current = null;
            const request = indexScroller.createRequest();
            const pin = {
              messageId: rowAnchor.node.id.replace('msg-hl-', ''),
              archiveTop: rowAnchor.top,
              archiveRequest: {
                attempt: request.attempt,
                cancel: () => {
                  request.cancel();
                  if (pin.retryTimer) clearTimeout(pin.retryTimer);
                  pin.retryTimer = null;
                },
              },
              until: Date.now() + 3000,
            };
            anchorPinRef.current = pin;
            repinAnchorNow();
            return;
          }
          const adjustedOffset = getChatPrependOffset(prepend, { contentHeight, element: el });
          prependCompensationRef.current = null;
          anchorPinRef.current = null;
          scrollOffsetRef.current = adjustedOffset;
          if (el) {
            el.scrollTop = adjustedOffset;
          }
          if (flatListRef.current?.scrollToOffset) {
            flatListRef.current.scrollToOffset({ offset: adjustedOffset, animated: false });
          }
          // The prepend is already compensated; correcting it again as drift
          // would push the reader down by the same height.
          if (el) chatScrollAnchor.sync(el);
          return;
        }

        // Keep pinning to the new bottom while a pin request is active. This is
        // what lets us reach the true end of long chats whose content grows as
        // markdown / code / math / images finish rendering asynchronously. The
        // stabilize timer is reset on each growth event so the pin outlasts the
        // rendering burst but releases soon after it ends.
        // Studio remounts can snap the list to 0. Restore that only when the
        // user is not reading history or asking for the tail — otherwise they
        // hit a wall on older messages and never reach the latest Work rows.
        if (isVideoStudioOpenRef.current) {
          restoreStudioScrollIfSnapped(el ? el.scrollTop : currentScrollY, el);
        }
        // Normal chat positioning pins stop once the reply has text.
        if (wantsBottomRef.current && !isVideoStudioOpenRef.current && getStreamScrollMode({
          hasStreamingText: !!streamingReplyRef.current?.id,
          followLive: followLiveRef.current,
          isCoworkerChat,
        }) !== WORK_STREAM_SCROLL.TAIL) {
          clearBottomPin();
        }
        if (isCoworkerChat && !anchorPinRef.current) {
          workScrollFollower.schedule();
        } else if (wantsBottomRef.current && flatListRef.current) {
          pinToBottomNow(false);
          if (contentHeight > prevContentHeight) bumpStabilizeTimer();
        } else if (
          followLiveRef.current
          && wasAtBottom
          && !anchorPinRef.current
          && flatListRef.current
          && contentHeight > prevContentHeight
        ) {
          // Streaming and Work activity can grow an existing row after the
          // short pin window expires. Keep the tail in view only while the
          // reader explicitly follows it (followLiveRef).
          pinToBottomNow(false);
        } else if (anchorPinRef.current && !prepend?.requestPending && flatListRef.current) {
          // Pending history tracks the current viewport. Unrelated reply
          // growth must not re-center its partially visible reading row.
          // A restore anchor is active: keep it pinned while async content
          // (markdown / code / images) reflows the rows above it.
          repinAnchorNow();
        }
        syncChatScrollAnchor(el);

        // Work in-place streams grow the list without moving scrollTop, so
        // handleScroll never runs to reveal jump-to-live. Recompute from the
        // post-pin offset so the control appears as soon as the reply
        // overflows, and hides again after a tail pin. Do not setState here
        // — RN Web invokes this during layout, and a Work follow-up's
        // start-pin + live row retriggered it until Maximum update depth.
        if (currentLayoutH > 0) {
          const postY = el ? el.scrollTop : currentScrollY;
          scheduleShowScrollButton(shouldShowScrollToBottomButton({
            contentHeight,
            layoutHeight: currentLayoutH,
            scrollTop: postY,
            bottomThreshold,
          }));
        }
      },
      onScrollToIndexFailed: indexScroller.onScrollToIndexFailed,
      onScrollBeginDrag: () => {
        hasUserDraggedRef.current = true;
        isUserScrollingRef.current = true;
        workScrollFollower.pause();
        userScrollIntentUntilRef.current = Date.now() + 1000;
        followLiveRef.current = false;
        // Any user-initiated drag cancels an active bottom-pin and aborts any
        // in-flight cold-start restore (anchor pin + pending scroll request) so
        // the user can freely scroll without being yanked back to the anchor.
        clearBottomPin();
        abortScrollRestore();
        // And if the user somehow starts dragging mid-veil, reveal immediately.
        endListSettleVeil();
      },
      // The finger lifted; momentum (if any) follows. Keep intent alive for
      // the momentum handoff so a flick that lands in the tail can re-arm.
      onScrollEndDrag: () => {
        isUserScrollingRef.current = false;
        userScrollIntentUntilRef.current = Date.now() + 1500;
        workScrollFollower.schedule();
      },
      onMomentumScrollBegin: () => {
        isUserScrollingRef.current = true;
      },
      onMomentumScrollEnd: () => {
        isUserScrollingRef.current = false;
        userScrollIntentUntilRef.current = Date.now() + 500;
        workScrollFollower.schedule();
      },
      onViewableItemsChanged: onViewableItemsChangedRef.current,
      viewabilityConfig: viewabilityConfigRef.current,
      onLayout: (event) => {
        const previousHeight = listLayoutHeightRef.current;
        const wasAtTail = previousHeight > 0 && contentHeightRef.current > 0
          && scrollOffsetRef.current + previousHeight >= contentHeightRef.current - 2;
        listLayoutHeightRef.current = event.nativeEvent.layout.height;
        // Preserve an already-visible tail when the Mac dock grows or shrinks.
        // Readers above the tail keep their position; a suggestion must not
        // restart live following or pull them away from an earlier message.
        if (Platform.OS === 'macos' && wasAtTail
          && previousHeight !== listLayoutHeightRef.current
          && !anchorPinRef.current && !isUserScrollingRef.current
          && Date.now() > userScrollIntentUntilRef.current) {
          pinToBottomNow(false);
        }
        if (isCoworkerChat) {
          workScrollFollower.schedule();
          return;
        }
        // Opening the video studio shrinks this column. Do not JS-pin — that
        // is the jump while Work is still streaming.
        if (isVideoStudioOpenRef.current) return;
        // Layout changes (e.g. keyboard opens) can shift content; honor any
        // active pin.
        if (wantsBottomRef.current || (followLiveRef.current && isAtBottomRef.current)) {
          if (getStreamScrollMode({
            hasStreamingText: !!streamingReplyRef.current?.id,
            followLive: followLiveRef.current,
            isCoworkerChat,
          }) === WORK_STREAM_SCROLL.TAIL) {
            pinToBottomNow(false);
          }
        }
      },
      ListFooterComponent: listFooterComponent,
      ListHeaderComponent: listHeaderComponent,
    };

    const props = {
      ...baseProps,
      removeClippedSubviews: false,
      updateCellsBatchingPeriod: 50,
    };

    if (Platform.OS === 'android' || Platform.OS === 'macos') {
      props.scrollEventThrottle = 16;
    }

    if (Platform.OS === 'web') {
      // Work rows are tall and relatively few. A normal window unmounts the
      // tail when you read the top, shrinks content height to the mounted
      // cells, and the list thinks it is already at the end — you cannot
      // reach the latest messages. RN Web also ignores disableVirtualization,
      // so keep a very large window instead.
      if (isCoworkerChat) {
        props.windowSize = 121;
        props.initialNumToRender = 40;
        props.maxToRenderPerBatch = 40;
      } else if (isLinuxDesktopWeb) {
        // Linux Chromium/Firefox often software-rasterize RN-web's View tree.
        // windowSize 101 keeps ~everything mounted and is what produced 500–800MB
        // tabs for long coding chats. Native default is 21. Mac/Windows keep the
        // large window — GPU compositing handles the extra layers, and shrinking
        // it historically left blank gaps in RN-web VirtualizedList.
        props.windowSize = 21;
        props.initialNumToRender = isCompact ? 10 : 12;
        props.maxToRenderPerBatch = isCompact ? 6 : 8;
      } else if (isCompact) {
        props.windowSize = 21;
        props.initialNumToRender = 15;
        props.maxToRenderPerBatch = 15;
      } else {
        props.windowSize = 31;
        props.initialNumToRender = 30;
        props.maxToRenderPerBatch = 30;
      }
    } else {
      props.windowSize = isXS ? 9 : 15;
      props.maxToRenderPerBatch = isXS ? 8 : 12;
      props.initialNumToRender = isXS ? 12 : 20;
    }

    // iOS maintainVisibleContentPosition is intentionally NOT set. It was the
    // root cause of long-chat viewport jolts: MVC re-pins a visible item on
    // every content reflow, which fights the bottom-pin (oscillation) and,
    // when MVC re-enables after a bottom-pin releases, snaps the viewport back
    // up toward the pinned index — the "bounces back up into the middle"
    // symptom. The bottom-pin + onContentSizeChange machinery keeps the
    // viewport at the bottom through async row growth, and archived-message
    // prepends are anchored explicitly via anchorPinRef (armed in
    // handleLoadEarlierMessages) so they no longer shift the viewport either.

    return props;
  }, [indexScroller, groupedMessages, listContentContainerStyle, initialLoadComplete, chat?.isInferencingWaiting, chat?.pendingInferenceStatus, chat?.pendingInferenceStage, chat?.pendingInferenceElapsedMs, chat?.pendingCanFastAnswer, chat?.summary, chat?.summarizedMessageCount, hasConversationSummaryPill, isInferencing, isXS, showsScrollIndicator, isSettleVeilUp, isCompact, isLinuxDesktopWeb, handleScroll, renderItem, isGhost, showCardExpirationWarning, id, listFooterComponent, listHeaderComponent, pinToBottomNow, workScrollFollower, clearBottomPin, restoreStudioScrollIfSnapped, bumpStabilizeTimer, repinAnchorNow, bumpListSettleVeil, endListSettleVeil, abortScrollRestore, isCoworkerChat, isVideoStudioOpen, chat?.wasUpgradedToWork, coworker.isActive, coworker.browserState, coworker.plan, coworker.events, coworker.launchWait, coworker.questionHistory, composerQuestion, workRuns, scheduleShowScrollButton, videoReviewQuestion, isDetailed, isAuthenticated, isOwner, recoverLinuxChatScroll, syncChatScrollAnchor, chatScrollAnchor]);

  // A run waiting on a sign-in gets the composer to itself: the user is being
  // asked to type credentials, and they must go to the browser rather than into
  // the thread, so the form stands where the message box would be.
  const workSignInRequest = isCoworkerChat && coworker.isActive
    ? coworker.browserState?.loginRequest || null
    : null;

  // Chat → Work swaps the model picker, voice, placeholder and action slot at
  // once. Fading the wrapper through zero means the relayout lands while the
  // bar is invisible; only the result is ever seen. Opacity alone keeps the
  // text input mounted, so focus and anything half-typed survive the switch.
  const hasNewTaskResponse = !!coworker.task?.handoffSource && (
    !!coworker.browserState?.question || !!coworker.browserState?.confirmation || !!workSignInRequest
    || chatData.messages.some(message => message.role === 'assistant'
      && (String(message.content || '').trim() || message.files?.length || message.parameters?.coworkerArtifacts?.length))
  );

  const chatInputComponent = useMemo(() => {
    if (storeTaskDetailsPending) return <ChatInputContainer><WorkTaskLoadingNotice unavailable={coworker.taskDetailsUnavailable} onRetry={coworker.retryTaskDetails} /></ChatInputContainer>;
    if (remoteMacConnection && remoteMacConnection !== 'connected') return <ChatInputContainer><RemoteMacComposerNotice status={remoteMacConnection} /></ChatInputContainer>;
    if (isMacProjectReadOnly) return <ChatInputContainer><MacProjectReadOnlyNotice name={coworker.task?.localProject?.name} /></ChatInputContainer>;
    return (
      <ChatInputContainer
        forceInputStyle={isCompact ? CHAT_INPUT_STYLE.FIXED : undefined}
        wrapperAnimatedStyle={workHandoverStyle}
      >
        {isCoworkerChat && (
          <>
            {/* Permission controls must remain visible while reading history,
                even when the virtualized live activity row is offscreen. */}
            {Platform.OS === 'macos' && <WorkAccessRequests key={coworker.taskId} taskId={coworker.taskId} />}
            {keyboardHeight <= 0 && (
              <CoworkerLivePanel
                taskId={coworker.taskId}
                isActive={coworker.isActive}
                taskStatus={coworker.taskStatus}
                onResume={coworker.resume}
                question={coworker.browserState?.question}
              />
            )}
            <WorkTaskBoundaryCard review={coworker.handoffReview} busy={coworker.boundaryChecking}
              sourceActive={coworker.isActive} onResolve={coworker.resolveHandoff} onCancel={coworker.cancelHandoff} />
            {keyboardHeight <= 0 && (
              <WorkTaskStartedNotice source={coworker.task?.handoffSource} taskId={coworker.taskId} autoDismiss={hasNewTaskResponse} />
            )}
            {!workSignInRequest && (
              <WorkFollowUpQueue
                isActive={coworker.isActive}
                waitingForLaunch={!!coworker.launchWait}
                items={coworker.followUpQueue}
                onEdit={coworker.updateFollowUp}
                onDelete={coworker.removeFollowUp}
                onReorder={coworker.reorderFollowUp}
                onSendNow={handleQueuedFollowUpNow}
                onClear={coworker.clearFollowUps}
              />
            )}
            {!workSignInRequest && <WorkNotificationNotice />}
          </>
        )}
        {workSignInRequest ? (
          <ScrollView
            style={styles.composerGate}
            keyboardDismissMode={Platform.isPad ? 'none' : (Platform.OS === 'ios' ? 'interactive' : 'on-drag')}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
            showsVerticalScrollIndicator={false}
          >
            <WorkSignInComposer taskId={coworker.taskId} loginRequest={workSignInRequest} />
          </ScrollView>
        ) : (
        <ChatInput
          key={id}
          onSend={waitingForMacAccess ? handleSendNow : handleSend}
          onCancel={handleCancelInferencing}
          showGhostBanner={isGhost}
          conversationId={id}
          collectionId={chat?.inCollectionId}
          forceInputStyle={isCompact ? CHAT_INPUT_STYLE.FIXED : undefined}
          hideVoiceMode={(isCompact && !voiceModeTarget) || isCoworkerChat}
          voiceModeTarget={voiceModeTarget}
          isWorkMode={isCoworkerChat}
          workGoal={coworker.task?.goal}
          onGoalSend={coworker.send}
          onGoalControl={coworker.controlGoal}
          goalControlsDisabled={coworker.boundaryChecking || coworker.taskStatus === 'queued'}
          // Work owns the meaning of the composer: steer while live, request
          // a revision while idle. Never let the generic per-chat placeholder
          // cached in input state replace that critical instruction.
          forcePlaceholder={isCompact || isCoworkerChat}
          isCompact={isCompact}
          sendWhileInferencing={isCoworkerChat}
          workFollowUpMode={isCoworkerChat && coworker.isActive && !waitingForMacAccess}
          onSendNow={handleSendNow}
          // Drive Stop from the same liveness as the follow-up queue.
          // chat.isInferencing can lag a live event log (stale idle snapshot).
          isInferencing={isCoworkerChat && coworker.isActive}
          withModels={!isCoworkerChat}
          placeholder={isCoworkerChat
            ? (waitingForMacAccess ? 'Reply to the agent…' : coworker.isActive ? WORK_STEER_PLACEHOLDER : WORK_FOLLOWUP_PLACEHOLDER)
            : (isImageGeneration ? 'Describe what you want to see...' : 'Type a message...')}
          isDisabled={isHeldForReview}
        />
        )}
      </ChatInputContainer>
    );
  }, [storeTaskDetailsPending, coworker.taskDetailsUnavailable, coworker.retryTaskDetails, remoteMacConnection, isMacProjectReadOnly, coworker.task?.localProject?.name, id, handleSend, handleSendNow, handleQueuedFollowUpNow, handleCancelInferencing, isGhost, chat?.inCollectionId, isImageGeneration, isCompact, voiceModeTarget, isHeldForReview, isCoworkerChat, workHandoverStyle, coworker.taskId, coworker.isActive, coworker.launchWait, coworker.taskStatus, coworker.resume, coworker.task?.goal, coworker.send, coworker.controlGoal, coworker.followUpQueue, coworker.updateFollowUp, coworker.removeFollowUp, coworker.reorderFollowUp, coworker.clearFollowUps, coworker.browserState?.question, coworker.handoffReview, coworker.task?.handoffSource, coworker.boundaryChecking, coworker.resolveHandoff, coworker.cancelHandoff, workSignInRequest, hasNewTaskResponse, keyboardHeight, waitingForMacAccess]);

  // A Mac project thread that never received its rows would otherwise render an
  // empty list. Show why it is read-only instead of a blank screen.
  if (isMacProjectReadOnly && !remoteMacConnection && !isLoading && !error && !visibleMessages.length) {
    return (
      <View style={styles.container}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 }}>
          {storeTaskDetailsPending
            ? <WorkTaskLoadingNotice unavailable={coworker.taskDetailsUnavailable} onRetry={coworker.retryTaskDetails} />
            : <MacProjectReadOnlyNotice name={coworker.task?.localProject?.name} />}
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.container}>
        <ChatLoadingState />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 }}>
          <Text color='primary' weight='bold' style={{ marginBottom: 8, textAlign: 'center' }}>
            {t('chat.notFoundTitle') || "Couldn't open this chat"}
          </Text>
          <Text color='secondary' style={{ marginBottom: 16, textAlign: 'center' }}>
            {(typeof error === 'string' ? error : error.message)
              || t('chat.notFoundBody') || 'Please try again in a moment.'}
          </Text>
          {onRetryError ? (
            <Button
              text={t('common.retry') || 'Try again'}
              variant="primary"
              size="medium"
              onPress={onRetryError}
            />
          ) : null}
          <View style={{ height: 10 }} />
          <Button
            text={t('chat.notFoundHome') || 'Go Home'}
            variant="secondary"
            size="medium"
            onPress={() => router.replace('/')}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.content} testID={`conversation-view-${id}`}>
      <View style={[styles.mainContent, { left: collapsedSidebarInset }]}>
        <BannerPlacement placement="chat-banner" isCompact={Platform.OS !== 'macos'} />
        <View
          ref={messagesContainerRef}
          style={styles.messagesContainer}
          onLayout={handleMessagesAreaLayout}
        >
          <View style={[
            styles.transcriptContainer,
            { marginRight: sourcesReservedRight },
            reservesMacComposer && { marginBottom: macComposerInset, overflow: 'hidden' },
          ]}>
            {isDetailed && (
              <View style={styles.chatInputDetailedContainer}>
                {chatInputComponent}
              </View>
            )}
            <Animated.View
              pointerEvents={isSettleVeilUp ? 'none' : 'auto'}
              style={[
                { flex: 1, opacity: listSettleOpacity },
                Platform.OS === 'web' && { minHeight: 0 },
                isSettleVeilUp && { overflow: 'hidden' },
              ]}
            >
              {/* Keyed on the thread alone: keying on Work-ness too used to
                  remount the whole list the moment a chat upgraded, throwing
                  away scroll position mid-transition. */}
              <WorkNotesVisibilityProvider key={id || 'chat'}>
                <WorkLiveProvider value={workLiveValue}>
                  {isCoworkerChat && Platform.OS === 'web' ? (
                    <WebWorkTranscriptList
                      listRef={flatListRef}
                      data={flatListProps.data}
                      renderItem={flatListProps.renderItem}
                      keyExtractor={flatListProps.keyExtractor}
                      onScroll={flatListProps.onScroll}
                      onContentSizeChange={flatListProps.onContentSizeChange}
                      onLayout={flatListProps.onLayout}
                      contentContainerStyle={flatListProps.contentContainerStyle}
                      ListHeaderComponent={flatListProps.ListHeaderComponent}
                      ListFooterComponent={flatListProps.ListFooterComponent}
                      style={flatListProps.style}
                    />
                  ) : (
                    <FlatList {...flatListProps} />
                  )}
                </WorkLiveProvider>
              </WorkNotesVisibilityProvider>
            </Animated.View>
            {isSettleVeilUp && (
              <Animated.View
                pointerEvents="none"
                style={[StyleSheet.absoluteFillObject, { opacity: settleSkeletonOpacity }]}
              >
                <ChatMessagesSkeleton />
              </Animated.View>
            )}
          </View>
          {showBrowserPanel && (
            <View style={{ width: '55%', minWidth: 360, alignSelf: 'stretch', borderLeftWidth: 1, borderLeftColor: colors.border_color_secondary }}>
              <BrowserViewer key={`${coworker.taskId}:${browserViewerWatch}`} threadId={coworker.taskId} onClose={closeBrowserViewer} initialWatch={browserViewerWatch} embedded />
            </View>
          )}
          {hasVisibleSourcesPanel && !showBrowserPanel && (
            <WorkSidebar
              key={`work-sidebar:${id}`}
              enabled={!isMacProjectReadOnly && isCoworkerChat && isAuthenticated && isOwner}
              availableHeight={messagesAreaHeight}
              bottomInset={timelineBottomInset}
              onOccupiedHeightChange={handleSourcesPanelHeightChange}
            >
              <SourcesOutputsPanel
                conversationId={id}
                taskId={coworker.taskId || null}
                hasProjectFolder={Platform.OS === 'macos' && !!coworker.task?.localProject?.bindingId}
                inputs={conversationInputs}
                outputs={conversationOutputs}
                sources={conversationSources}
                showEmptySources={conversationSources.length === 0}
                collectionId={chat?.inCollectionId}
                availableHeight={messagesAreaHeight}
                onOccupiedHeightChange={handleSourcesPanelHeightChange}
                cloudBrowser={workCloudBrowser}
                videoStudio={workVideoStudio}
                showTasks={!isMacProjectReadOnly && isCoworkerChat && isAuthenticated && isOwner}
                canAddTask={workChatTasks.canAddTask}
                tasks={workChatTasks.tasks}
                onAddTask={workChatTasks.addTask}
                onEditTask={workChatTasks.editTask}
                onSetTaskEnabled={workChatTasks.setTaskEnabled}
                onDeleteTask={workChatTasks.removeTask}
              />
            </WorkSidebar>
          )}
          {showTimelineRail && !showBrowserPanel && timelinePrompts.length >= 2 && (
            <ConversationTimelineRail
              prompts={timelinePrompts}
              activePromptId={activeTimelinePromptId}
              onPromptPress={handleTimelinePromptPress}
              topInset={timelineTopInset}
              bottomInset={timelineBottomInset}
            />
          )}
          {!isCompact && (
            <ConversationFindBar
              key={id}
              conversationId={id}
              messages={chatData.messages}
            />
          )}
        </View>
        {(isAuthenticated && isOwner) && (
          <ChatComposerOverlay
            ref={inputContainerRef}
            getScrollElement={getScrollElement}
            showsScrollIndicator={showsScrollIndicator}
            conversationId={id}
            ready={!isLoading && !error}
            sourcesReservedRight={sourcesReservedRight}
            showBrowserPanel={showBrowserPanel}
            style={styles.inputContainer}
            onLayout={handleInputContainerLayout}
          >
            <Animated.View style={styles.scrollToBottomButtonContainer}>
              <Button
                style={styles.scrollToBottomButton}
                variant="secondary"
                size="small"
                isRound={true}
                prependIcon="arrow-down"
                text={workJumpToLiveLabel || undefined}
                textStyle={workJumpToLiveLabel ? styles.scrollToBottomLiveText : undefined}
                onPress={scrollToBottom}
                tooltip={workJumpToLiveLabel ? 'Jump to live' : 'Scroll to bottom'}
                accessibilityLabel={workJumpToLiveLabel ? `Jump to live: ${workJumpToLiveLabel}` : 'Scroll to bottom'}
              />
            </Animated.View>

            {showFollowUpsBar && !isDetailed && !isMacProjectReadOnly && (
              <FollowUpSuggestions
                suggestions={followUpSuggestions.suggestions}
                onSuggestionPress={handleFollowUpSuggestionPress}
              />
            )}
            {!isDetailed && (
              <View style={styles.composerStack}>
                {!isMacProjectReadOnly && isCoworkerChat && coworker.browserState?.confirmation ? (
                  <WorkAppear style={workColumnStyle} duration={220} distance={6}>
                    <WorkApprovalCard
                      taskId={coworker.taskId}
                      confirmation={coworker.browserState.confirmation}
                      onDismissQuestion={coworker.dismissPendingGate}
                      onRestoreQuestion={coworker.restorePendingGate}
                    />
                  </WorkAppear>
                ) : null}
                {!isMacProjectReadOnly && isCoworkerChat && composerQuestion ? (
                  <ScrollView
                    style={[workColumnStyle, styles.composerGate]}
                    keyboardDismissMode={Platform.isPad ? 'none' : (Platform.OS === 'ios' ? 'interactive' : 'on-drag')}
                    keyboardShouldPersistTaps="handled"
                    nestedScrollEnabled
                    showsVerticalScrollIndicator={false}
                  >
                    <WorkAppear duration={220} distance={6}>
                      <WorkQuestionCard
                        key={composerQuestion?.id || coworker.taskId}
                        taskId={coworker.taskId}
                        question={composerQuestion}
                        onAnswered={(text, extra) => {
                          if (String(composerQuestion?.id || '').startsWith('video-review-')) {
                            setVideoReviewHiddenGate(videoProjectReviewGate(videoStudioProject, { events: coworker.events }));
                          }
                          return handleWorkQuestionAnswered(text, extra);
                        }}
                        onDismissQuestion={coworker.dismissPendingGate}
                        onRestoreQuestion={coworker.restorePendingGate}
                      />
                    </WorkAppear>
                  </ScrollView>
                ) : null}
                <ChatCollectionBadge collectionId={chat?.inCollectionId} />
                {showCollectionSuggestionBar && (
                  <CollectionSuggestionBanner
                    suggestion={collectionSuggestion}
                    isResponding={isRespondingToCollectionSuggestion}
                    onAccept={acceptCollectionSuggestion}
                    onDismiss={dismissCollectionSuggestion}
                  />
                )}
                {!isCoworkerChat && (
                  <>
                    <WorkModeIntentSuggestions
                      contextId={id}
                      variant="conversation"
                      onActivate={handleActivateWorkFromComposer}
                    />
                    <ToolIntentSuggestions contextId={id} variant="conversation" />
                  </>
                )}
                {chatInputComponent}
              </View>
            )}
          </ChatComposerOverlay>
        )}
      </View>
      {/* Browser viewer: web can take control; native watches without
          interrupting the agent's browser input. */}
      {!isMacProjectReadOnly && !showBrowserPanel && isCoworkerChat && !!coworker.taskId && browserViewerTaskId === coworker.taskId && (
        <BrowserViewer key={`${coworker.taskId}:${browserViewerWatch}`} threadId={coworker.taskId} onClose={closeBrowserViewer} initialWatch={browserViewerWatch} />
      )}
    </View>
  );
});

const renderChatView = props => <ChatView {...props} />;

export default function ChatViewWithSideChat(props) {
  return <SideChatLayout {...props} renderChat={renderChatView} />;
}
